/* ================================================================
   regions/us/content.ts — all site copy & data for the United States
   site (us.juristab.com). Components import it through
   src/config/content.ts; its shape is checked against au's.

   JurisTab in the U.S. is Case Management and Workflow only: the legal
   research product (the case-law and legislation corpus) is not offered
   here, so nothing below mentions it. Prices are the live U.S. catalogue
   (juristab-backend-integration/regions/us/billing.yaml), in U.S. dollars,
   written with money() so the symbol comes from region.json.
   ================================================================ */

import region from './region.json';
import { formatMoney, currencySymbol, phoneHref, RegionProfile } from '../../market';
import type { CalcProfession, CalcRow, MarketNotice } from '../../contentTypes';

const REGION: RegionProfile = region;

/** A price in U.S. dollars: money(39) → "$39", money(0.1, 2) → "$0.10". */
const money = (amount: number, decimals = 0) => formatMoney(REGION, amount, decimals);
const APP_URL = REGION.apps.juristab;
/** Shown before a plan's price, "$" — the hero says which dollars. */
const CURRENCY_SYMBOL = currencySymbol(REGION);

/* ── Shared / Global ── */

export const SITE = {
  name: REGION.site.name,
  tagline: 'Law made readable and clear.',
  copyright: '© 2026 JurisTab. All rights reserved.',
  appUrl: APP_URL,
  docuDiveUrl: REGION.apps.docudive,
};

export const CONTACT_INFO = {
  email: REGION.contact.email,
  emailHref: `mailto:${REGION.contact.email}`,
  phone: REGION.contact.phone,
  phoneHref: REGION.contact.phone ? phoneHref(REGION.contact.phone) : '',
  location: REGION.contact.location,
};

export const SOCIAL_LINKS = REGION.social;

/* ── Navbar ── */

export const NAV_ITEMS = [
  { label: 'Home', href: '/' },
  { label: 'Pricing', href: '/pricing' },
  {
    label: 'Resources',
    dropdown: [
      { label: 'About Us', desc: 'Our mission & team', href: '/about' },
      { label: 'Case Management', desc: 'Matters, time, billing & e-signatures', href: '/case-management' },
      { label: 'Workflow', desc: 'Document automation & contract review', href: '/workflow-features' },
      { label: 'Practice Areas', desc: 'Family, criminal defense & real estate suites', href: '/practice-areas' },
      { label: 'Blogs', desc: 'Insights & updates', href: '/blogs' },
    ],
  },
  {
    label: 'Data Privacy & Governance',
    dropdown: [
      { label: 'Responsible AI', desc: 'Ethics, principles & the 5P governance framework', href: '/responsible-ai' },
      { label: 'Data Privacy', desc: 'How we secure client data on AWS', href: '/data-privacy' },
    ],
  },
  {
    label: 'Tools & Calculators',
    dropdown: [
      { label: 'In-House Counsel', desc: 'ROI for corporate legal departments', href: '/calculators/in-house' },
      { label: 'Law Firms', desc: 'ROI on billable capacity', href: '/calculators/private-practice' },
      { label: 'Real Estate Closings', desc: 'ROI on closing throughput', href: '/calculators/real-estate' },
    ],
  },
  {
    label: 'Products',
    dropdown: [
      { label: 'JurisTab', desc: 'Case management & AI workflow', href: APP_URL, external: true },
    ],
  },
  { label: 'Contact', href: '/contact' },
];

/* ── Home page ── */

export const HOME = {
  hero: {
    badge: 'AI-Powered · Case Management & Workflow',
    title: 'Empowering Attorneys\nand Law Firms with\n',
    titleAccent: 'AI Solutions',
    subtitle:
      'Run your matters end to end — client intake, conflict checks, calendars, meetings, time, billing with IOLTA trust accounting, and e-signatures — then let AI do the document work on the same files: contract review and redlining, risk reports, and dedicated suites for family law, criminal defense and real estate practice.',
    ctaPrimary: 'Explore JurisTab →',
    ctaPrimaryLink: APP_URL,
    ctaSecondary: 'Watch a demo',
    ctaSecondaryLink: '/contact',
    pills: [
      { icon: '\u{1F4BC}', text: 'Case & matter management' },
      { icon: '\u{1F9E9}', text: 'Document workflow automation' },
      { icon: '\u{1F512}', text: 'Hosted in the U.S. on AWS' },
    ],
    stats: [
      { value: '45', label: 'AI workflow services' },
      { value: '3', label: 'Practice-area suites' },
      { value: '50', label: 'States, plus D.C. and federal' },
    ],
  },

  loginCard: {
    signUp: {
      title: 'Get started free',
      subtitle: 'Enter your email to create an account.',
      button: 'Continue with Email',
      footerText: 'Already have an account?',
      footerAction: 'Sign in →',
    },
    signIn: {
      title: 'Welcome back',
      subtitle: 'Sign in to your JurisTab workspace.',
      button: 'Sign In',
      footerText: "Don’t have an account?",
      footerAction: 'Sign up →',
    },
    emailLabel: 'Email',
    emailPlaceholder: 'you@yourfirm.com',
    passwordLabel: 'Password',
    passwordPlaceholder: '••••••••••',
  },

  /* The two modules JurisTab ships in the U.S., as stacked rows. `icon` is a
     key into PILLAR_ICONS in Home.tsx. */
  pillars: {
    tag: 'Platform',
    title: 'Two modules,',
    titleAccent: 'one workspace',
    subtitle:
      'Run the practice and automate the document work — case management and workflow automation under one login, sharing the same matters, the same files and one bill.',
    rows: [
      {
        index: '01',
        name: 'Case Management',
        tagline: 'Run the practice — first inquiry to final invoice',
        desc:
          'The matter file itself: clients, deadlines, tasks, meetings, time, documents, trust money and bills in one place — and the same matters the workflow services read from, so nothing is entered twice.',
        link: '/case-management',
        linkText: 'Explore Case Management →',
        items: [
          { icon: 'userPlus',  label: 'Client Intake & Conflicts',    desc: 'Inquiry to client, conflict-checked' },
          { icon: 'briefcase', label: 'Case & Matter Management',     desc: 'One live file per matter' },
          { icon: 'folder',    label: 'Document & File Management',   desc: 'Every file in one tree' },
          { icon: 'invoice',   label: 'Billing & IOLTA Trust',        desc: 'Draft bill to sent invoice' },
          { icon: 'clock',     label: 'Time & Expense Tracking',      desc: 'Live timer, tenth-of-an-hour entries' },
          { icon: 'video',     label: 'Tasks & Online Meetings',      desc: 'Video rooms clients join by link' },
          { icon: 'calendar',  label: 'Legal Calendaring',            desc: 'Court deadlines, synced to Outlook' },
          { icon: 'signature', label: 'Electronic Signatures',        desc: 'Send, sign and store in the file' },
        ],
      },
      {
        index: '02',
        name: 'Workflow Automation',
        tagline: '45 agentic services — including family law, criminal defense and real estate suites',
        desc:
          'Point a service at the documents already in the matter and get work back — a redlined contract, a risk register, a marital estate schedule, a sentencing memorandum — with sources cited and the exact cost shown before anything runs.',
        link: '/workflow-features',
        linkText: 'See every service →',
        items: [
          { icon: 'checkSquare', label: 'Contract Review & Redline',   desc: 'Clause by clause, with redline' },
          { icon: 'alert',       label: 'Risk & Issues Report',        desc: 'Every risk found and ranked' },
          { icon: 'columns',     label: 'Clause Comparison',           desc: 'Two drafts, one clean diff' },
          { icon: 'penTool',     label: 'Legal Argument Draft',        desc: 'First drafts on your letterhead' },
          { icon: 'bell',        label: 'Key Dates Extraction',        desc: 'Every deadline in a document' },
          { icon: 'counsel',     label: 'Counsel',                     desc: 'Chat across the whole matter' },
          { icon: 'table',       label: 'Document Matrix Review',      desc: 'Many documents, one table' },
          { icon: 'layers',      label: 'Practice-Area Suites',        desc: 'Family, criminal defense, real estate' },
        ],
      },
    ],
  },

  /* Dedicated practice-area service suites, plus the cross-practice tools
     beside them. Service names match the U.S. catalogue
     (workflow-automation packs/us) exactly. */
  suites: {
    tag: 'Built for Your Practice',
    title: 'Services that know ',
    titleAccent: 'your practice area',
    subtitle:
      'Beyond the general services, three practice areas get their own suite — built around the documents those matters actually run on. Every line of output links back to the page it came from, and any arithmetic is done in code, not guessed by the model.',
    link: '/practice-areas',
    linkText: 'See every practice area →',
    items: [
      {
        id: 'family-law',
        name: 'Family Law',
        count: '14 services',
        desc: 'Financial disclosures through to the marital settlement agreement, with the marital estate and every figure traced to its source.',
        services: [
          'Financial Disclosure Cross-Check',
          'Marital Estate Schedule',
          'Declaration / Affidavit Draft',
          'Marital Settlement Agreement & Stipulated Judgment',
          'Domestic Violence & Risk Screening',
          'Retirement Asset Division (QDRO) Check',
        ],
      },
      {
        id: 'criminal-defense',
        name: 'Criminal Defense',
        count: '13 services',
        desc: 'From discovery to sentencing — set to the offense’s jurisdiction: federal, any of the 50 states, or D.C.',
        services: [
          'Discovery & Evidence Review',
          'Charge Analysis',
          'Custodial Interrogation / Miranda Review',
          'Discovery / Brady Request Letter',
          'Pretrial Release / Bail Motion',
          'Sentencing Memorandum (Mitigation)',
        ],
      },
      {
        id: 'real-estate',
        name: 'Real Estate',
        count: '6 services',
        desc: 'Purchase agreements, contingencies, seller disclosures and title commitments — reviewed, drafted and explained for the client.',
        services: [
          'Purchase Agreement & Disclosure Review',
          'Purchase Agreement Addenda & Contingencies Draft',
          'Title Commitment & Exceptions Memo',
          'Seller Disclosure Statement Builder',
        ],
      },
    ],
    tools: [
      {
        icon: 'counsel',
        name: 'Counsel',
        desc: 'Ask questions across up to 50 documents in a matter. Answers come with inline citations, and Counsel can hand back Word or Excel files on your letterhead.',
      },
      {
        icon: 'checkSquare',
        name: 'Contract Review & Redline',
        desc: 'Review a contract clause by clause against your position, then get a tracked-changes redline back in Word — ready to send to opposing counsel.',
      },
      {
        icon: 'table',
        name: 'Document Matrix Review',
        badge: 'Beta',
        desc: 'Put many documents against your own questions. Each cell is answered with reasoning and page citations, then exported to Excel.',
      },
    ],
  },

  showcase: {
    tag: 'In Action',
    title: 'See how ',
    titleAccent: 'JurisTab',
    titleSuffix: '\nworks in practice',
    subtitle:
      'One workspace where the matter file and the document work point at the same client, the same deadlines and the same documents — so a fact entered once is never entered again.',
    checklist: [
      'Case management — intake and conflict checks, matters, tasks and online meetings, calendaring, time, invoicing, IOLTA trust accounts and e-signatures in one file',
      '45 workflow services — contract review with redline, risk reports, summaries, clause extraction and key dates',
      'Family law, criminal defense and real estate suites built on the documents those matters run on',
      'Counsel — ask questions across up to 50 matter documents and get cited answers, Word and Excel files back',
      'Every job quotes its exact cost before it runs, and every output links back to its source page',
    ],
    ctaPrimary: 'Try it now →',
    ctaPrimaryLink: APP_URL,
    ctaSecondary: 'View all services',
    ctaSecondaryLink: '/workflow-features',
    badgeIcon: '⚡',
    badgeLabel: 'Every job',
    badgeValue: 'Priced before it runs',
    imgAlt: 'JurisTab product screenshot',
  },

  products: {
    tag: 'Our Platform',
    title: 'Two modules, one ',
    titleAccent: 'subscription',
    subtitle:
      'Take the module your practice lives in, or both together on Pro+ — the same matters, the same documents and one bill either way.',
    items: [
      {
        label: 'Run the Practice',
        name: 'Case Management',
        desc: 'Intake and conflict checks, matters, contacts, calendars, meetings, time and expenses, invoicing with IOLTA trust accounting, intake forms, precedents and e-signatures — everything a law firm runs on, in one file per matter.',
        link: APP_URL,
        linkText: 'Open JurisTab →',
        featured: true,
      },
      {
        label: 'Automate the Documents',
        name: 'Workflow',
        desc: '45 agentic services that read, review, draft and organize legal documents — contract review and redlining, risk reports, matrix review and Counsel — plus dedicated family law, criminal defense and real estate suites.',
        link: APP_URL,
        linkText: 'Open JurisTab →',
        featured: false,
      },
    ],
  },

  resources: {
    tag: 'Resources',
    title: 'Everything you need\nto ',
    titleAccent: 'get started',
    subtitle:
      'From AI governance documentation to platform guides — everything your firm needs to adopt JurisTab with confidence.',
    items: [
      {
        icon: '\u{1F3DB}️',
        title: 'About JurisTab',
        desc: 'Learn about our mission to give attorneys and law firms AI that runs the practice and takes on the document work.',
        tag: 'ABOUT',
        link: '/about',
      },
      {
        icon: '⚖️',
        title: 'Responsible AI',
        desc: 'Our commitment to ethical AI. Read about the principles, frameworks, and safeguards that govern how JurisTab uses artificial intelligence.',
        tag: 'GOVERNANCE',
        link: '/responsible-ai',
      },
      {
        icon: '\u{1F512}',
        title: 'Data Privacy',
        desc: 'How JurisTab secures client data on AWS — encryption, access controls, U.S. data residency, and zero training on your documents.',
        tag: 'SECURITY',
        link: '/data-privacy',
      },
      {
        icon: '\u{1F4BC}',
        title: 'Case Management',
        desc: 'Running the practice — client intake and conflict checks, matters and parties, documents, time and expenses, invoicing and trust accounting, calendaring, online meetings and e-signatures.',
        tag: 'CASE MANAGEMENT',
        link: '/case-management',
      },
      {
        icon: '\u{1F9E9}',
        title: 'Workflow',
        desc: 'Document automation — contract review with redlining, risk reports, summaries, Counsel, and the family law, criminal defense and real estate suites.',
        tag: 'WORKFLOW',
        link: '/workflow-features',
      },
      {
        icon: '\u{1F5C2}️',
        title: 'Practice Areas',
        desc: 'Dedicated workflow suites for family law, criminal defense and real estate, plus document workflows for corporate, employment, estates and every other practice area.',
        tag: 'PRACTICE AREAS',
        link: '/practice-areas',
      },
      {
        icon: '\u{1F4B3}',
        title: 'Pricing Plans',
        desc: `Pay as you go, or take one module — case management from ${money(39)} or workflow from ${money(119)} per user a month. Every job quotes its cost before it runs.`,
        tag: 'PRICING',
        link: '/pricing',
      },
      {
        icon: '\u{1F4D6}',
        title: 'Blog & Insights',
        desc: 'Articles on AI governance for attorneys, document automation, and running a more efficient law practice.',
        tag: 'BLOG',
        link: '/blogs',
      },
      {
        icon: '\u{1F4AC}',
        title: 'Contact & Support',
        desc: `Reach out for support or inquiries. Email us at ${CONTACT_INFO.email} or use the contact form.`,
        tag: 'SUPPORT',
        link: '/contact',
      },
    ],
  },

  blogs: {
    tag: 'Insights',
    title: 'From the ',
    titleAccent: 'JurisTab',
    titleSuffix: ' journal',
    viewAllText: 'View all articles →',
    viewAllLink: '/blogs',
    readMore: 'Read article →',
    items: [
      {
        emoji: '\u{1F9E9}',
        thumbClass: 'blogThumb1' as const,
        category: 'AI & Legal Tech',
        date: '2025',
        title: '5 Ways to Streamline Your Legal Workflow',
        desc: 'Practical ways attorneys can cut the busywork out of document review, deadlines and matter admin.',
        featured: true,
        slug: 'streamline-legal-workflow',
      },
      {
        emoji: '⚖️',
        thumbClass: 'blogThumb2' as const,
        category: 'AI & Legal Tech',
        date: '2025',
        title: 'AI Governance: Ethics in Legal Technology',
        desc: 'The ethical duties and governance frameworks attorneys need for responsible AI use — from ABA Formal Opinion 512 to firm policy.',
        featured: false,
        slug: 'ai-governance-ethics-legal-tech',
      },
      {
        emoji: '\u{1F4BB}',
        thumbClass: 'blogThumb3' as const,
        category: 'Industry News',
        date: '2025',
        title: 'Digital Transformation in the Legal Industry',
        desc: 'How digital transformation is reshaping law firms and what they need to know to stay competitive.',
        featured: false,
        slug: 'digital-transformation-legal-industry',
      },
    ],
  },

  cta: {
    title: 'Run the matter,\nautomate ',
    titleAccent: 'the documents',
    titleSuffix: '',
    subtitle:
      'Join attorneys running case management and document workflow in one place. Start free with 1,000 trial credits — no credit card required.',
    ctaPrimary: 'Create Free Account',
    ctaPrimaryLink: APP_URL,
    ctaSecondary: 'Contact Us',
    ctaSecondaryLink: '/contact',
  },
};

/* ── About page ── */

export const ABOUT = {
  hero: {
    label: 'About Us',
    title: 'About JurisTab',
    subtitle: 'Law made readable and clear.',
  },

  description: {
    label: 'Who We Are',
    title: 'Redefining Legal Technology',
    text: 'A legal-tech platform for attorneys, law firms and in-house legal departments. JurisTab brings case management and AI document workflow into one workspace — from opening the matter and running the conflict check to redlining the contract and sending the final invoice.',
  },

  mission: {
    title: 'Our Mission',
    text: 'To break down barriers of cost and complexity in legal technology, so firms of every size can run their practice and their document work with the same tools as the largest firms.',
  },

  vision: {
    title: 'Our Vision',
    text: 'To give every attorney back the hours lost to administration and document busywork, creating a more efficient, accessible and equitable justice system for all.',
  },

  targetUsers: {
    label: 'Built For',
    title: 'Who We Serve',
    subtitle: 'Giving every part of the legal practice intelligent tools.',
    items: [
      {
        title: 'Law Firms',
        description:
          'Solo attorneys, small firms and growing practices that need one system for matters, time, billing and trust money — and AI that takes on the document work.',
      },
      {
        title: 'In-House Legal Departments',
        description:
          'Corporate legal teams reviewing contracts at volume, tracking obligations and deadlines, and bringing outside-counsel work back in-house.',
      },
      {
        title: 'Paralegals & Legal Staff',
        description:
          'The people who run the file — intake, calendaring, document organization and billing — with less retyping and fewer missed deadlines.',
      },
    ],
  },

  products: {
    label: 'Our Platform',
    title: 'Powerful Tools for Legal Minds',
    subtitle: 'Two modules designed to transform how you run your practice and handle its documents.',
    items: [
      {
        title: 'Case Management',
        description:
          'Client intake and conflict checks, matters, contacts, calendaring, online meetings, time and expenses, invoicing with IOLTA trust accounting, intake forms, precedents and e-signatures — one live file for every matter.',
        link: APP_URL,
      },
      {
        title: 'Workflow',
        description:
          '45 AI workflow services — including dedicated family law, criminal defense and real estate suites — and Counsel for questions across a matter, all working on the documents already in the file.',
        link: APP_URL,
      },
    ],
  },

  cta: {
    title: 'Ready to Transform Your Practice?',
    subtitle: 'Join attorneys who trust JurisTab to run their matters and automate their document work.',
    ctaPrimary: 'Get Started Free',
    ctaPrimaryLink: APP_URL,
    ctaSecondary: 'Contact Us',
    ctaSecondaryLink: '/contact',
  },
};

/* ── Pricing page ── */

export const PRICING = {
  hero: {
    label: 'Pricing',
    title: 'Simple Pricing, Real Numbers',
    subtitle:
      'Start with 1,000 free trial credits. Then pay as you go, or subscribe to the module you actually live in — case management or document workflow, or both on Pro+. All prices in U.S. dollars, per user, and you always see the exact cost before a job runs.',
  },

  featuredBadge: 'Best value',

  plans: [
    {
      name: 'Pay As You Go',
      description: 'No monthly fee. Buy credits, spend them only when you run something.',
      price: '35',
      currency: CURRENCY_SYMBOL,
      period: '/ 350 credits',
      features: [
        `10 credits = ${money(1)}. Packs: ${money(35)} = 350 credits, ${money(99)} = 1,090, ${money(265)} = 3,180`,
        `Workflow: 1–3 credits per page (${money(0.1, 2)}–${money(0.3, 2)}) depending on the service`,
        'Case management: 15 credits per contact record — matters and time are free',
        'Credits stay valid for 365 days — and pause if you subscribe later',
        `Example: a 60-page contract review = 180 credits = ${money(18)}`,
      ],
      cta: 'Buy Credits',
      ctaLink: APP_URL,
    },
    {
      name: 'Case Management',
      description: 'Run the practice: intake, matters, calendar, time, billing, trust and signing.',
      price: '39',
      currency: CURRENCY_SYMBOL,
      period: '/ user / month',
      features: [
        'Unlimited matters, contacts, tasks and legal calendaring',
        'Time and expense recording, invoices and IOLTA trust accounting',
        'Documents, intake forms, e-signatures and reports across every matter',
        `${money(390)} a year — two months free`,
        'Workflow still available, charged from credits',
        `Example: 30 new contacts a month costs ${money(45)} on credits — ${money(39)} here, unlimited`,
      ],
      cta: 'Start Case Management',
      ctaLink: APP_URL,
    },
    {
      name: 'Workflow',
      description: 'For document-heavy practices: review, redline, extract, draft.',
      price: '119',
      currency: CURRENCY_SYMBOL,
      period: '/ user / month',
      features: [
        '1,200 processed pages included every month',
        'Every service costs one allowance page per page — light or heavy',
        `Extra pages about ${money(0.1, 2)} each, settled on your next invoice`,
        `${money(1190)} a year — two months free`,
        'Up to 100 pages per document, 10 documents per job, 2 jobs at once',
        'One brand template, so generated documents come out on your letterhead',
      ],
      cta: 'Start Workflow',
      ctaLink: APP_URL,
    },
    {
      name: 'Pro+',
      description: 'Case Management and Workflow together, with our largest page allowance.',
      price: '179',
      currency: CURRENCY_SYMBOL,
      period: '/ user / month',
      features: [
        'Less than Case Management and Workflow bought separately',
        'Unlimited case management',
        '3,000 processed pages a month, plus 750 pages of rollover',
        `Extra pages about ${money(0.06, 2)} each, settled on your next invoice`,
        `${money(1790)} a year — two months free`,
        'Up to 250 pages per document, 20 documents per job, 5 jobs at once',
        'Five brand templates for generated documents',
      ],
      cta: 'Get Pro+',
      ctaLink: APP_URL,
      featured: true,
    },
  ],

  guide: {
    label: 'What things cost',
    title: 'The Rate Card, in Plain Numbers',
    subtitle:
      `Credits are the single unit of usage: 10 credits = ${money(1)}. Workflow is charged per processed page, case management per new contact, and nothing runs before you have seen the price.`,
    table: {
      title: 'Rate Card',
      headers: ['What you run', 'Rate', 'In dollars', 'Worked example'],
      rows: [
        {
          from: 'Upload, OCR, matter organization, export to Word or PDF',
          to: 'Free',
          charged: `${money(0)} — always`,
          how: 'Reading and organizing your own files is never metered, on any plan.',
        },
        {
          from: 'Matters, tasks, calendar entries, time and expense records, invoices',
          to: 'Free',
          charged: `${money(0)} — always`,
          how: 'Running the matter file costs nothing. Only a new contact record is metered.',
        },
        {
          from: 'Contact record — case management',
          to: '15 credits',
          charged: `${money(1.5, 2)} a contact`,
          how: 'Charged once, when the contact is created. Unlimited on Case Management and Pro+.',
        },
        {
          from: 'Light workflow — Document Organizer, Annotation & Highlights, Party & Defined Terms, Plain-Language Explainer',
          to: '1 credit a page',
          charged: `${money(0.1, 2)} a page`,
          how: `A 20-page bundle = 20 credits = ${money(2)}.`,
        },
        {
          from: 'Standard workflow — Risk & Issues Report, Document Matrix Review, Document Summary, Clause Extraction, Obligation Checklist, Key Dates',
          to: '2 credits a page',
          charged: `${money(0.2, 2)} a page`,
          how: `A 40-page report = 80 credits = ${money(8)}.`,
        },
        {
          from: 'Heavy workflow — Contract Review & Redline, Clause Comparison, Legal Argument Draft, practice-area suite services',
          to: '3 credits a page',
          charged: `${money(0.3, 2)} a page`,
          how: `A 60-page contract = 180 credits = ${money(18)}.`,
        },
      ],
    },
    sections: [
      {
        title: 'How credits work',
        points: [
          `10 credits = ${money(1)}. Every price on this page converts at that rate.`,
          `Buy in packs: ${money(35)} = 350 credits, ${money(99)} = 1,090 credits, ${money(265)} = 3,180 credits.`,
          `Larger packs carry a bonus — the ${money(265)} pack works out at 12 credits per dollar instead of 10.`,
          'Credits are valid for 365 days from purchase.',
          'Subscribe later and that countdown pauses; if the plan ends, it restarts from the same number of days.',
          'Turn on auto top-up and we buy your usual pack whenever your balance drops below 100 credits.',
        ],
      },
      {
        title: 'A month in the life',
        points: [
          'A real estate attorney opens 5 new client contacts, runs one 60-page purchase agreement review and one 40-page risk report.',
          `On pay as you go that is 75 + 180 + 80 credits = 335 credits, or ${money(33.5, 2)} for the month.`,
          'At that volume credits are still cheaper than any subscription — so stay on pay as you go.',
          `Case Management at ${money(39)} overtakes credits at about 26 new contacts a month.`,
          `Workflow at ${money(119)} overtakes credits past roughly 400 pages of heavy review a month.`,
          'Your dashboard does this comparison for you each month and tells you which is cheaper.',
        ],
      },
      {
        title: 'Allowances and overage',
        points: [
          'On Workflow and Pro+ every processed page costs one allowance page — light or heavy, it makes no difference.',
          'The full month of pages lands the day you subscribe, whatever the date.',
          'Go past the allowance and we use your credits next; only when those run out do you pay overage.',
          `Overage is the plan’s own page rate — about ${money(0.1, 2)} a page on Workflow, about ${money(0.06, 2)} on Pro+ — added to your next invoice.`,
          'Set a monthly overage cap and we pause new jobs at the cap rather than quietly billing more.',
          'Pro+ rolls up to 25% of an unused allowance — 750 pages — into the next month only. It is not cumulative.',
        ],
      },
      {
        title: 'Upgrading, downgrading, canceling',
        points: [
          'Upgrade any time: the new features and the full new allowance unlock immediately, and your renewal date does not move.',
          `You pay only the difference, prorated for the days left — moving from Workflow to Pro+ with 10 of 30 days remaining costs about ${money(20)}.`,
          'A downgrade costs nothing now and takes effect at your next renewal; the plan you paid for runs to the end.',
          'One plan change per billing period, so the change you make is the one that sticks.',
          'Cancel and you keep everything you paid for until the period ends, then move to pay as you go.',
          'A failed payment never cancels anything — your current plan stays active while you retry or update your card.',
        ],
      },
      {
        title: 'When a plan ends',
        points: [
          'Unused allowance and rollover pages are forfeited — they belong to the period they were granted for.',
          'Purchased credits are untouched, and their 365-day clock restarts from exactly where it paused.',
          'Matters, documents, outputs, invoices and history stay readable. Nothing is deleted.',
          'You are on pay as you go from that moment, and can start new work as soon as you have credits.',
        ],
      },
      {
        title: 'Trials, quotes and refunds',
        points: [
          'New accounts get 1,000 trial credits, valid for 7 days. No card required.',
          'That is enough to take two matters end to end before you decide anything.',
          'Every job shows its exact cost before it runs, and you are never charged more than that quote.',
          'If a job fails on our side, the credits go straight back to the bucket they came from.',
          'Unused credits are refundable within 7 days of purchase, provided less than 10% of the pack has been used.',
          'Subscription fees are never paid with credits, and credits are never spent on a subscription — the two meters stay separate.',
        ],
      },
    ],
  },

  faq: {
    label: 'FAQ',
    title: 'Frequently Asked Questions',
    subtitle: 'Straight answers about credits, allowances and plans',
    items: [
      {
        question: 'Which plan should I be on?',
        answer:
          `Start on pay as you go — there is no monthly fee, so you only pay for what you run. Move to Case Management (${money(39)}) once you are opening more than about 26 new contacts a month. Move to Workflow (${money(119)}) once you process more than about 400 pages of heavy review a month. Take Pro+ (${money(179)}) if you need both — it costs less than Case Management and Workflow bought separately, with a bigger page allowance. Your dashboard compares last month’s usage against every plan and tells you which would have been cheapest.`,
      },
      {
        question: 'What is a credit worth?',
        answer:
          `10 credits = ${money(1)}, so one credit is 10 cents. A new contact record is 15 credits (${money(1.5, 2)}), and workflow pages are 1 to 3 credits each (${money(0.1, 2)}–${money(0.3, 2)}) depending on how heavy the service is. Packs start at ${money(35)} for 350 credits, and larger packs include bonus credits — ${money(265)} buys 3,180 credits, which is 12 credits per dollar.`,
      },
      {
        question: 'What does case management cost to run?',
        answer:
          `Almost nothing on pay as you go. Matters, tasks, the calendar, time and expense records, documents, invoices, trust transactions and e-signature envelopes are not metered at all — only creating a new contact record is, at 15 credits (${money(1.5, 2)}). So a practice that opens ten new clients a month spends ${money(15)} on credits. Past roughly 26 new contacts a month the ${money(39)} Case Management plan, which makes all of it unlimited, is the cheaper answer. Pro+ includes case management as well.`,
      },
      {
        question: 'What counts as a processed page?',
        answer:
          'A page that a workflow service actually reads and works on. Uploading, OCR, organizing a matter and exporting are never counted. On the credit rate card a page costs 1, 2 or 3 credits depending on the service. On a subscription it is simpler: every processed page uses exactly one page of your monthly allowance, whether it came from the lightest or the heaviest service.',
      },
      {
        question: 'Do credits expire?',
        answer:
          'Purchased credits are valid for 365 days. If you subscribe, the countdown pauses for as long as your plan runs — you are using the plan’s allowance, not your credits, so it would be unfair to burn the clock. If the plan later ends, the countdown restarts from exactly the number of days it had left. Trial credits are different: they last 7 days from activation and do not pause.',
      },
      {
        question: 'What happens if I go over my monthly allowance?',
        answer:
          `Nothing stops. We use your allowance first, then any credits you hold, and only after both are exhausted do you pay overage — about ${money(0.1, 2)} a page on Workflow and about ${money(0.06, 2)} a page on Pro+ — which is added to your next invoice. You can set a monthly overage cap; when you reach it we pause new jobs and tell you, instead of continuing to bill. Pro+ also rolls up to 750 unused pages into the following month.`,
      },
      {
        question: 'Is there a free trial?',
        answer:
          'Yes. New accounts receive 1,000 trial credits valid for 7 days, with no card required. That is sized to take two matters end to end — open the clients, run a bundle through three services and ask Counsel a few questions — so you can judge the output on your own files. When the trial ends you simply move to pay as you go: you keep read access to everything you produced, and can start new work again as soon as you buy credits or subscribe.',
      },
      {
        question: 'Can I switch plans at any time?',
        answer:
          'Yes. Upgrades take effect immediately: the full new allowance is granted straight away, your renewal date stays where it is, and you pay only the price difference prorated across the days remaining. Downgrades cost nothing now and take effect at your next renewal, so you keep the plan you have already paid for until the end of its period. You can make one plan change per billing period.',
      },
      {
        question: 'Do you charge sales tax?',
        answer:
          'No sales tax is added to JurisTab plans or credit packs — the price you see is the price you pay. Inside case management, your own client invoices default to no tax as well, since most states do not tax legal services; if your state does, set its rate in billing settings and it is applied to your invoices.',
      },
      {
        question: 'What if a job fails, or I bought credits I do not need?',
        answer:
          'If a job fails on our side it is refunded automatically, back to the same credits or allowance it was taken from. If it failed because of the file or input you supplied, it is refunded automatically for 30 days and by request after that. Unused credit packs are refundable within 7 days of purchase as long as you have used less than 10% of them, refunded pro rata on the unspent portion.',
      },
      {
        question: 'What payment methods do you accept?',
        answer:
          'All major credit and debit cards — Visa, Mastercard, American Express and Discover — processed securely through Stripe. Cards can be saved for renewals and auto top-ups. Firms and enterprise legal departments can also be invoiced; talk to us and we will set it up.',
      },
    ],
  },

  cta: {
    title: 'Firms, Legal Departments and Enterprise',
    subtitle:
      'Seat-based billing, volume page rates, invoicing and onboarding. Tell us roughly how many matters and pages your team runs each month and we will price it for you.',
    button: 'Talk To Us',
    buttonLink: '/contact',
  },
};

/* ── Case Management page ── */

export const CASE_MANAGEMENT_PAGE = {
  hero: {
    label: 'Case Management',
    title: 'The Whole Matter, ',
    titleAccent: 'in One File',
    subtitle:
      'Client intake and conflict checks, matters and parties, documents, time and expenses, invoicing and IOLTA trust accounting, calendaring, online meetings and e-signatures — the practice management your firm runs on, sharing the same matters as the AI workflow it sits beside.',
  },

  section: {
    label: 'What We Manage',
    title: 'Eleven Modules. One Matter File.',
    subtitle: 'Click a module to see how it handles its part of running the practice.',
  },

  contentNotAvailable: 'Content not available.',

  cta: {
    title: 'Ready to run your matters in one place?',
    subtitle:
      `Case management from ${money(39)} per user a month, or nearly free on pay as you go until you are opening more than about 26 new contacts a month — matters, time and invoicing are never metered.`,
    button: 'Sign Up',
    buttonLink: APP_URL,
  },
};

/* ── Features page ──
   The research page (/features) is not part of the U.S. build
   (region.json markets omitRoutes); this section only satisfies the
   shared content shape and is never rendered. */

export const FEATURES_PAGE = {
  hero: { label: '', title: '', titleAccent: '', subtitle: '' },
  section: { label: '', title: '', subtitle: '' },
  contentNotAvailable: 'Content not available.',
  cta: { title: '', subtitle: '', button: '', buttonLink: '/' },
};

/* ── Workflow page ── */

export const WORKFLOW_FEATURES_PAGE = {
  hero: {
    label: 'Workflow',
    title: 'Document Work, ',
    titleAccent: 'Automated',
    subtitle:
      'Agentic AI that reads, reviews, drafts, and organizes legal documents end to end. From redlined contract reviews to sentencing memoranda, with dedicated suites for family law, criminal defense and real estate practice — built for the way law firms actually work.',
  },

  section: {
    label: 'What We Automate',
    title: '45 Services. One Workflow Engine.',
    subtitle: 'Click a service or practice-area suite to see how it streamlines a specific stage of legal document work.',
  },

  contentNotAvailable: 'Content not available.',

  cta: {
    title: 'Ready to automate your document workflow?',
    subtitle:
      'Combine these services into pipelines that handle review, drafting, and organization — so your team can focus on judgment, not busywork.',
    button: 'Sign Up',
    buttonLink: APP_URL,
  },
};

/* ── Blogs page ── */

export const BLOGS_PAGE = {
  hero: {
    label: 'Knowledge Hub',
    title: 'Blog & Insights',
    subtitle: 'Explore the latest in legal technology, AI in law firm practice, and industry trends.',
  },
  categories: ['All', 'AI & Legal Tech', 'Industry News'],
  loading: 'Loading blogs...',
  noPosts: 'No posts found.',
  imgAlt: 'Blog Image',
};

/* ── Blog post page ── */

export const BLOG_POST_PAGE = {
  loading: 'Loading...',
  notFound: 'Post not found',
  backText: 'Back to Blog',
  backLink: '/blogs',
  shareLabel: 'Share',
  relatedTitle: 'Related Posts',
  shareTitle: 'Share This Post',
};

/* ── Contact page ── */

export const CONTACT = {
  hero: {
    label: 'Contact',
    title: 'Get in Touch',
    subtitle: 'Have a question or want to learn more? We would love to hear from you.',
  },

  form: {
    sectionTitle: "Let's Start a Conversation",
    sectionDesc:
      'Whether you have a question about our products, need technical support, or want to explore partnership opportunities, our team is here to help.',
    socialLabel: 'Follow Us',
    fields: {
      firstName: { label: 'First Name', placeholder: 'John' },
      lastName: { label: 'Last Name', placeholder: 'Doe' },
      email: { label: 'Email', placeholder: 'john@example.com' },
      phone: { label: 'Phone', placeholder: '(212) 555-0123' },
      subject: {
        label: 'Subject',
        placeholder: 'Select a subject',
        options: [
          'General Inquiry',
          'Technical Support',
          'Sales & Pricing',
          'Partnerships',
          'Feedback',
          'Other',
        ],
      },
      message: { label: 'Message', placeholder: 'Tell us how we can help...' },
    },
    submitButton: 'Send Message',
    sendingButton: 'Sending…',
  },

  /* No U.S. office to map yet (region.json office is null). */
  map: REGION.office && {
    label: REGION.office.label,
    query: REGION.office.label,
    title: REGION.office.mapTitle,
    latitude: REGION.office.latitude,
    longitude: REGION.office.longitude,
    directionsUrl: REGION.office.directionsUrl,
  },
};

/* ── Responsible AI: 5P governance tab ── */

export const RESPONSIBLE_AI_GOVERNANCE = {
  key: 'ai-governance',
  label: 'AI Governance',
  title: 'JurisTab\'s 5P Governance Framework',
  subtitle: 'Policy, Protection, Platform, Proof and Persistence: five pillars governing every AI system across its lifecycle.',
  image: '/images/responsible-ai/ai-governance-5p.jpg',
  vision: 'To reap AI benefits ethically, safely, and responsibly.',
  intro:
    'The 5P Framework establishes a comprehensive governance structure that enables JurisTab to harness the transformative power of AI while maintaining the highest standards of ethics, security, privacy, and operational excellence, aligned with the NIST AI Risk Management Framework and the professional obligations of the attorneys who use it.',
  frameworkIntro:
    'This framework ensures our AI systems — including the workflow services, Counsel, contract review and redlining, and the practice-area suites — operate transparently, accurately, and in line with U.S. privacy law, ABA guidance on generative AI, and international best practices.',
  frameworkDefinition: [
    { label: 'WHY', description: 'To ensure all AI initiatives deliver value while protecting customers, employees, and the organization from AI-related risks.' },
    { label: 'WHAT', description: 'A governance framework covering 5 pillars: Policy, Protection, Platform, Proof, and Persistence.' },
    { label: 'WHEN', description: 'Applied throughout the AI lifecycle: from project initiation, through development and deployment, to ongoing operations.' },
    { label: 'HOW', description: 'Through the AI Council governance structure, standardized risk assessments, the 5P Checklist, and continuous monitoring.' },
    { label: 'WHO', description: 'The AI Council provides oversight. Technology leadership and the AI Engineering Lead drive implementation.' },
    { label: 'WHERE', description: 'All JurisTab products and services. Covers cloud-hosted, on-premises, and hybrid AI solutions.' },
  ],
  /* Descriptions support inline [label](url) links */
  items: [
    {
      title: 'Policy',
      category: 'Legal, Privacy & Compliance',
      description:
        'Alignment with U.S. AI risk guidance and privacy law, privacy by design, responsible data governance, and the professional-conduct rules that apply to legal practice.',
      subItems: [
        { title: 'U.S. AI Risk Guidance', description: 'Alignment with the [NIST AI Risk Management Framework](https://www.nist.gov/itl/ai-risk-management-framework) and its [Generative AI Profile](https://www.nist.gov/publications/artificial-intelligence-risk-management-framework-generative-artificial-intelligence).' },
        { title: 'Privacy by Design', description: 'Data minimization and purpose limitation in line with state privacy laws such as the [California Consumer Privacy Act (CCPA)](https://oag.ca.gov/privacy/ccpa), and FTC guidance on AI and data.' },
        { title: 'Data Governance', description: 'PII handling across storage, processing, transmission, and retention; data minimization; consent management.' },
        { title: 'Legal Holds & Investigations', description: 'Data handling and retention policies supporting security investigations and litigation holds.' },
        { title: 'Professional Responsibility', description: 'Built to support attorneys’ duties of competence, confidentiality and supervision under [ABA Formal Opinion 512](https://www.americanbar.org/news/abanews/aba-news-archives/2024/07/aba-issues-first-ethics-guidance-ai-tools/) and the ABA Model Rules.' },
      ],
    },
    {
      title: 'Protection',
      category: 'Security & Guardrails',
      description:
        'Security by design, defense in depth, AI-specific guardrails, bounded agentic AI, and integrated security operations.',
      subItems: [
        { title: 'Cybersecurity by Design', description: 'Security integrated from inception through design, build, test and release; threat modeling, vulnerability assessments, tooling, penetration testing.' },
        { title: 'Defense in Depth', description: 'Multiple security layers, network segmentation, access controls, IP filtering and firewall protection, encryption at rest and in transit.' },
        { title: 'AI-Specific Guardrails', description: 'Prompt injection prevention, output filtering, content safety, model access controls, ethical and responsible AI controls.' },
        { title: 'Agentic AI Framework', description: 'Agent boundaries, permission models, human-in-the-loop controls, autonomous action limits.' },
        { title: 'SecOps Integration', description: 'Security operations center integration, incident response, threat monitoring, SOC alerting.' },
      ],
    },
    {
      title: 'Platform',
      category: 'Architecture & Build Standards',
      description:
        'Enterprise architecture alignment, multi-agent communication, integration and infrastructure standards, and disciplined model management.',
      subItems: [
        { title: 'Enterprise Architecture', description: 'EA capability assessment, alignment with enterprise standards, and technology roadmap integration.' },
        { title: 'Agent-to-Agent (A2A)', description: 'Communication standards for multi-agent systems, protocol definitions, message formats.' },
        { title: 'Integration Standards', description: 'API standards, data exchange formats, service mesh requirements, event-driven architecture.' },
        { title: 'Model Management', description: 'Model versioning, model registry, deployment pipelines, rollback capabilities.' },
        { title: 'Infrastructure Standards', description: 'Cloud architecture patterns, compute requirements, scaling policies, cost management.' },
      ],
    },
    {
      title: 'Proof',
      category: 'Test, Deploy, Evaluate & Monitor',
      description:
        'Evidence before trust: testing agents and models against business outcomes, evaluation and bias benchmarks, performance testing, observability, and quality gates.',
      subItems: [
        { title: 'Testing Approach', description: 'Testing for agents, LLM models, and integrations, measuring accuracy and output against expected business outcomes.' },
        { title: 'Evaluation Framework', description: 'Model evaluation standards, benchmark definitions, performance metrics, bias detection.' },
        { title: 'Performance Testing (SVT)', description: 'Stress and volume testing, load testing, latency benchmarks, throughput requirements.' },
        { title: 'Observability & Monitoring', description: 'Monitoring standards, tooling & framework selection, alerting thresholds, dashboards.' },
        { title: 'Quality Gates', description: 'Acceptance criteria, go/no-go decision frameworks, release certification.' },
      ],
    },
    {
      title: 'Persistence',
      category: 'Operate, Resilience, Audit & Optimize',
      description:
        'Keeping AI dependable over time: high availability and recovery, integrated service management, full traceability, retention, and continuous improvement.',
      subItems: [
        { title: 'Operational Excellence', description: 'High Availability (HA), Disaster Recovery (DR), defined RTO and RPO targets.' },
        { title: 'Enterprise Integration', description: 'Incident Management, Problem Management, and Change Management processes.' },
        { title: 'Logging & Auditing', description: '[OpenTelemetry](https://opentelemetry.io/) implementation, full traceability of customer/agent transactions across all solution components.' },
        { title: 'Retention Policy', description: 'Log retention policy definition and implementation, compliance with legal hold requirements.' },
        { title: 'Analytics for Improvement', description: 'Analysis of user types and prompts/requests for continuous improvement and optimization.' },
      ],
    },
  ],
  council: {
    intro:
      'The AI Council provides cross-functional oversight and governance for all AI initiatives across JurisTab.',
    roles: [
      { role: 'AI Council', responsibility: 'Strategic oversight, policy approval, risk acceptance for HIGH/CRITICAL projects, dispute resolution.', cadence: 'Monthly + ad hoc for escalations' },
      { role: 'AI Engineering Lead', responsibility: 'Framework ownership, project portfolio oversight, vendor management.', cadence: 'Weekly reviews' },
      { role: 'Security Lead', responsibility: 'Protection assessment, security architecture review, SecOps integration, incident response.', cadence: 'Per project' },
      { role: 'Privacy/Legal Lead', responsibility: 'Policy assessment, privacy impact reviews, legal risk evaluation, policy alignment.', cadence: 'Per project' },
      { role: 'Enterprise Architect', responsibility: 'Architecture review, integration standards, platform assessment, technical debt management.', cadence: 'Per project' },
      { role: 'Project Managers', responsibility: 'Project delivery, 5P checklist completion, risk identification, stakeholder management.', cadence: 'Continuous' },
      { role: 'Vendors & Partners', responsibility: 'Comply with the 5P Framework, provide the required documentation, and participate in reviews.', cadence: 'Per project requirements' },
    ],
    keyPrinciple:
      'Leaders cannot delegate or outsource accountability for AI systems. While tasks may be delegated to vendors or team members, accountability for AI governance remains with designated JurisTab leaders, consistent with the Govern function of the [NIST AI Risk Management Framework](https://www.nist.gov/itl/ai-risk-management-framework).',
  },
  workflow: [
    'Register project in the AI Project Register',
    'Complete the 5P Assessment Checklist',
    'Submit to the AI Engineering Lead for review',
    'Obtain required approvals based on risk level',
    'Deploy and maintain continuous monitoring',
  ],
  references: {
    intro: 'The 5P Framework is inspired by and aligned with the following U.S. and international standards:',
    groups: [
      {
        title: 'U.S. Frameworks & Guidance',
        items: [
          { name: 'NIST AI Risk Management Framework', description: 'Four core functions: Govern, Map, Measure, Manage — plus the Generative AI Profile.', link: 'https://www.nist.gov/itl/ai-risk-management-framework' },
          { name: 'ABA Formal Opinion 512', description: 'The ABA’s guidance on generative AI tools: competence, confidentiality, communication, supervision and fees.', link: 'https://www.americanbar.org/news/abanews/aba-news-archives/2024/07/aba-issues-first-ethics-guidance-ai-tools/' },
          { name: 'ABA Model Rules of Professional Conduct', description: 'Including Rule 1.1 (competence, and comment 8 on technology), Rule 1.6 (confidentiality) and Rules 5.1/5.3 (supervision).', link: 'https://www.americanbar.org/groups/professional_responsibility/publications/model_rules_of_professional_conduct/' },
          { name: 'California Consumer Privacy Act (CCPA)', description: 'The leading state privacy law, including rights over personal information and automated decision-making.', link: 'https://oag.ca.gov/privacy/ccpa' },
        ],
      },
      {
        title: 'International Standards',
        items: [
          { name: 'ISO/IEC 42001:2023', description: 'AI Management System standard for establishing, implementing, and improving AI management.', link: 'https://www.iso.org/standard/42001' },
          { name: 'EU AI Act', description: 'Risk-based regulatory framework classifying AI systems by risk level.', link: 'https://artificialintelligenceact.eu/' },
          { name: 'OECD AI Principles', description: 'International principles for trustworthy AI adopted by 46 countries including the United States.', link: 'https://oecd.ai/en/ai-principles' },
        ],
      },
      {
        title: 'Technical Standards',
        items: [
          { name: 'NIST Cybersecurity Framework', description: 'Govern, Identify, Protect, Detect, Respond, Recover functions.', link: 'https://www.nist.gov/cyberframework' },
          { name: 'OpenTelemetry', description: 'Observability framework for traces, metrics, and logs.', link: 'https://opentelemetry.io/' },
          { name: 'TOGAF', description: 'Enterprise architecture framework for business, data, application, and technology architecture.', link: 'https://www.opengroup.org/togaf' },
          { name: 'ITIL 4', description: 'IT service management framework for incident, problem, and change management.', link: 'https://www.axelos.com/certifications/itil-service-management' },
        ],
      },
    ],
  },
};

/* ── Responsible AI page ── */

export const RESPONSIBLE_AI = {
  hero: {
    label: 'Responsible AI',
    title: 'Responsible AI at JurisTab',
    subtitle:
      'Our commitment to ethical, transparent, and accountable AI in legal technology.',
  },

  tabs: [
    {
      key: 'ai-ethics',
      label: 'AI Ethics',
      title: 'Ethical AI in Legal Technology',
      subtitle: 'How JurisTab upholds ethical standards across every AI-powered feature.',
      image: '/images/responsible-ai/ai-ethics.png',
      intro:
        'At JurisTab, ethical AI is not an afterthought — it is embedded into the design, development, and deployment of every feature. We believe that AI in the legal domain carries heightened responsibility because the outcomes directly affect clients’ rights, liberty and livelihoods.',
      items: [
        {
          title: 'Fairness & Non-Discrimination',
          description:
            'Our AI services are regularly evaluated for bias across matter types and parties. We ensure that reviews, summaries and drafts do not favor or disadvantage any party unfairly.',
        },
        {
          title: 'Transparency & Explainability',
          description:
            'Every AI-generated output — from a risk report to a redline — includes provenance indicators so users can trace how a result was derived and verify it against the source document.',
        },
        {
          title: 'User Privacy & Consent',
          description:
            'No training occurs on client documents or prompts. Your work product stays yours, consistent with attorneys’ duty of confidentiality under ABA Model Rule 1.6.',
        },
        {
          title: 'Human-Centered Design',
          description:
            'AI assists rather than replaces professional legal judgment. Human oversight remains central to every workflow, so attorneys keep full control of — and responsibility for — their work product.',
        },
      ],
    },
    RESPONSIBLE_AI_GOVERNANCE,
    {
      key: 'ai-principles',
      label: 'AI Principles',
      title: 'Our Core AI Principles',
      subtitle: 'The foundational principles that guide every AI decision at JurisTab.',
      image: '/images/responsible-ai/ai-principles.png',
      intro:
        'Our AI principles are informed by the NIST AI Risk Management Framework and ABA guidance on generative AI, and shaped by the unique demands of the legal profession. They serve as the north star for product development, model selection, and platform operations.',
      items: [
        {
          title: 'Accuracy Above All',
          description:
            'Legal AI must be accurate. Every output is tied to page citations in the source documents, any arithmetic is done in code rather than by the model, and confidence thresholds flag what needs a closer look.',
        },
        {
          title: 'Accountability & Auditability',
          description:
            'Every AI decision is logged and traceable. We maintain comprehensive audit trails so that outputs can be reviewed, challenged, and explained at any point.',
        },
        {
          title: 'Proportionality',
          description:
            'AI is deployed only where it adds genuine value without introducing disproportionate risk. We carefully assess each use case to ensure AI augments — never undermines — the integrity of legal work.',
        },
        {
          title: 'Continuous Improvement',
          description:
            'Our models and safeguards evolve with the legal landscape. We regularly review performance benchmarks, user feedback, and regulatory changes to refine our AI systems.',
        },
      ],
    },
    {
      key: 'strategic-imperatives',
      label: 'Strategic Imperatives',
      title: 'Strategic AI Imperatives',
      subtitle: 'The strategic commitments driving JurisTab\'s responsible AI roadmap.',
      image: '/images/responsible-ai/strategic-imperatives.png',
      intro:
        'Beyond principles and governance, JurisTab is committed to a set of strategic imperatives that shape our long-term AI roadmap. These imperatives ensure that our technology serves the evolving needs of U.S. attorneys and law firms while maintaining the highest standards of responsibility.',
      items: [
        {
          title: 'U.S. Data Residency',
          description:
            'Customer data for U.S. accounts is processed and stored in the United States, on AWS in the US East (N. Virginia) region, and AI processing stays on U.S.-hosted infrastructure.',
        },
        {
          title: 'Open Standards & Interoperability',
          description:
            'We build on open standards wherever possible, ensuring that JurisTab fits existing legal workflows — Word, Outlook, e-signature and accounting exports.',
        },
        {
          title: 'Industry Collaboration',
          description:
            'We engage with bar associations, legal technologists and academic institutions to co-develop AI practices that reflect the realities of legal practice in the United States.',
        },
        {
          title: 'Resilience & Business Continuity',
          description:
            'Our platform is designed for high availability with incident management procedures, retention policies aligned with legal requirements, and failover mechanisms that keep your matters and documents available.',
        },
      ],
    },
  ],

  cta: {
    title: 'Trust Starts with Transparency',
    subtitle:
      'We believe responsible AI is not optional — it is foundational. Explore how JurisTab puts ethical AI into practice.',
    ctaPrimary: 'Get Started Free',
    ctaPrimaryLink: APP_URL,
    ctaSecondary: 'Contact Us',
    ctaSecondaryLink: '/contact',
  },
};

/* ── Data Privacy & Security page ── */

export const DATA_PRIVACY = {
  hero: {
    label: 'Data Privacy & Security',
    title: 'Your Data, Protected by Design',
    subtitle:
      'How JurisTab secures client data on AWS — encryption, access controls, U.S. data residency, and the safeguards behind every workflow.',
  },

  summary: {
    intro:
      'JurisTab is built on Amazon Web Services (AWS), the same infrastructure trusted by global banks, government agencies, and major law firms. Every layer of our platform — from storage and transmission to AI processing and access — is engineered around the principle that client data is privileged data. This page summarizes the technical and organizational measures we take to keep it that way.',
    pillars: [
      { label: 'Hosted on AWS', value: 'Tier-1 cloud infrastructure with ISO 27001, SOC 1/2/3, and PCI DSS certifications inherited from AWS.' },
      { label: 'Encrypted End-to-End', value: 'AES-256 encryption at rest; TLS 1.3 in transit. Keys managed through AWS KMS.' },
      { label: 'U.S. Data Residency', value: 'Customer data for U.S. accounts is processed and stored in the AWS US East (N. Virginia) region, in the United States.' },
      { label: 'Zero Training on Client Data', value: 'We never use client documents, prompts, or workflow outputs to train AI models. Ever.' },
    ],
  },

  /* One line under each tab's name in the sidebar. */
  tabDescriptions: {
    infrastructure: 'AWS US East region, physical security, and network isolation.',
    encryption: 'AES-256 at rest, TLS 1.3 in transit, RBAC, and audit logs.',
    compliance: 'State privacy laws, ABA guidance, and AWS-inherited certifications.',
    continuity: 'Automated backups, multi-AZ failover, and incident response.',
    'ai-confidentiality': 'No training on your data; zero-retention LLM agreements.',
  } as Record<string, string>,

  tabs: [
    {
      key: 'infrastructure',
      label: 'Infrastructure & Hosting',
      title: 'Built on AWS — the Global Standard for Secure Cloud',
      subtitle: 'Tier-1 cloud infrastructure with U.S. data residency and proven physical security.',
      image: '/images/data-privacy/infrastructure.png',
      intro:
        'JurisTab is hosted entirely on Amazon Web Services (AWS). AWS operates one of the most rigorously secured cloud environments in the world — used by financial institutions, governments, healthcare providers, and law firms globally. By building on AWS, JurisTab inherits decades of investment in physical, network, and operational security that no in-house solution can match.',
      items: [
        {
          title: 'AWS US East (N. Virginia) Region',
          description:
            'All customer data for U.S. accounts — documents, matters, account information, AI outputs — is stored and processed in the AWS US East (N. Virginia) region (us-east-1), in the United States.',
        },
        {
          title: 'Multi-Availability-Zone Deployment',
          description:
            'Production services run across multiple AWS Availability Zones within the region. Each AZ is a physically separate data center with independent power, cooling, and networking — ensuring continuous availability even if a single facility experiences a disruption.',
        },
        {
          title: 'Physical & Environmental Security',
          description:
            'AWS data centers feature 24/7 security staff, biometric access controls, video surveillance, intrusion detection, fire suppression, and redundant power. AWS audits these controls under SOC 1, SOC 2, SOC 3, ISO 27001, ISO 27017, ISO 27018, and PCI DSS — JurisTab inherits the benefit of every one.',
        },
        {
          title: 'Network Isolation (VPC)',
          description:
            'JurisTab’s services run inside a dedicated AWS Virtual Private Cloud (VPC) with private subnets, security groups, and network ACLs. Databases and storage are not exposed to the public internet — all access is mediated through authenticated API endpoints behind AWS API Gateway.',
        },
      ],
    },
    {
      key: 'encryption',
      label: 'Encryption & Access',
      title: 'Encryption at Every Layer, Access on a Need-to-Know Basis',
      subtitle: 'Industry-standard encryption, strict identity controls, and full audit logging of every action.',
      image: '/images/data-privacy/encryption.png',
      intro:
        'No data leaves JurisTab unencrypted, and no data sits at rest unencrypted. Access is governed by the principle of least privilege: people and systems get only the permissions they need, only for as long as they need them, and every access is logged.',
      items: [
        {
          title: 'Encryption at Rest (AES-256)',
          description:
            'All customer data — documents in Amazon S3, structured data in our databases, search indices, and backups — is encrypted using AES-256, the same standard adopted by governments and financial institutions worldwide. Encryption keys are managed through AWS Key Management Service (KMS) with hardware security modules (HSMs).',
        },
        {
          title: 'Encryption in Transit (TLS 1.3)',
          description:
            'Every connection — between you and JurisTab, between our services, and between JurisTab and AWS APIs — is encrypted using TLS 1.3, the latest and most secure version of the protocol. We enforce HTTPS-only access; downgrades to weaker cipher suites are blocked.',
        },
        {
          title: 'Role-Based Access Control (RBAC)',
          description:
            'Both customers and JurisTab personnel operate under role-based access control. Customer accounts isolate data to the authorized user or organization. Internal access to production systems is restricted to a small group of engineers, requires multi-factor authentication, and is granted on a least-privilege, time-bound basis.',
        },
        {
          title: 'Comprehensive Audit Logging',
          description:
            'Every administrative action, API call, and data access event is logged through AWS CloudTrail and our application audit pipeline. Logs are stored in tamper-resistant S3 buckets with retention policies aligned to legal-industry expectations and are available for incident review and compliance reporting.',
        },
      ],
    },
    {
      key: 'compliance',
      label: 'Compliance & Standards',
      title: 'Aligned with U.S. Privacy Law and Global Standards',
      subtitle: 'State privacy laws, attorneys’ confidentiality duties, and the certifications inherited from AWS.',
      image: '/images/data-privacy/compliance.png',
      intro:
        'JurisTab is designed to support the obligations U.S. attorneys face — the duty of confidentiality under ABA Model Rule 1.6 and its state counterparts, ABA Formal Opinion 512 on generative AI, and state privacy and data-breach laws. The platform also benefits from the global certifications AWS maintains — covering security, availability, and confidentiality — so your data sits within infrastructure independently audited against the world’s most demanding standards.',
      items: [
        {
          title: 'State Privacy Law Alignment',
          description:
            'JurisTab’s data handling practices are built around the principles common to U.S. state privacy laws such as the California Consumer Privacy Act — notice, purpose limitation, access, correction, deletion and security. Our Privacy Policy details what we collect, why, and your rights.',
        },
        {
          title: 'AWS Inherited Certifications',
          description:
            'By hosting on AWS, JurisTab benefits from AWS’s certifications and attestations: ISO 27001 (information security), ISO 27017 (cloud security), ISO 27018 (cloud privacy), SOC 1, SOC 2, SOC 3, PCI DSS, FedRAMP-authorized services, and HIPAA-eligible services. These cover the physical, network, and infrastructure layers your data lives on.',
        },
        {
          title: 'Privacy by Design',
          description:
            'Privacy is built into the platform from the ground up — not retrofitted. We practice data minimization (collect only what’s necessary), purpose limitation (use data only for the stated purpose), and storage limitation (retain only as long as required). New features pass a privacy impact review before release.',
        },
        {
          title: 'Data Residency',
          description:
            'Customer data for U.S. accounts is stored and processed in the United States. Where AI processing requires interaction with third-party large language model providers, those interactions run on U.S.-hosted infrastructure under zero-retention and no-training contractual terms — covered in the AI & Confidentiality tab.',
        },
      ],
    },
    {
      key: 'continuity',
      label: 'Backup & Continuity',
      title: 'Resilient by Design, Recoverable by Default',
      subtitle: 'Automated backups, multi-AZ failover, and incident response procedures that keep you operating.',
      image: '/images/data-privacy/continuity.png',
      intro:
        'Data security is not just confidentiality — it is also availability and integrity. JurisTab’s infrastructure is engineered for high availability, with automated backups, multi-zone failover, and a defined incident response process so that disruptions are short, recoveries are clean, and you keep working.',
      items: [
        {
          title: 'Automated, Encrypted Backups',
          description:
            'Databases are backed up automatically with point-in-time recovery enabled. Document storage in S3 uses cross-AZ replication and versioning, so deleted or corrupted files can be recovered. All backups are encrypted with AES-256 and stored in the United States.',
        },
        {
          title: 'High Availability & Failover',
          description:
            'Critical services are deployed across multiple AWS Availability Zones. If one zone experiences an outage, traffic automatically shifts to healthy zones without customer intervention. Defined RTO (Recovery Time Objective) and RPO (Recovery Point Objective) targets govern our disaster recovery posture.',
        },
        {
          title: 'Continuous Monitoring & Alerting',
          description:
            'AWS CloudWatch, security monitoring services, and our application observability stack continuously watch for anomalies — failed logins, unusual data access patterns, performance degradation, and security events. Alerts page on-call engineers within minutes of detection.',
        },
        {
          title: 'Incident Response & Notification',
          description:
            'We maintain a documented incident response procedure covering detection, containment, eradication, recovery, and post-incident review. In the unlikely event of a data incident affecting customer data, we will notify affected customers in line with applicable state data-breach notification laws.',
        },
      ],
    },
    {
      key: 'ai-confidentiality',
      label: 'AI & Confidentiality',
      title: 'Your Documents Stay Yours — Including from the AI',
      subtitle: 'Zero training on client data, tenant isolation, and zero-retention agreements with AI providers.',
      image: '/images/data-privacy/ai-confidentiality.png',
      intro:
        'Client documents are privileged. The most common concern we hear from attorneys is what happens to documents and prompts once AI gets involved — whether they end up training someone’s model, leaking into another user’s results, or being retained by a third party. The answer at JurisTab is straightforward: they do not.',
      items: [
        {
          title: 'No Training on Client Data',
          description:
            'We do not use customer documents, prompts, or workflow outputs to train, fine-tune, or improve any AI model — neither our own nor any third party’s. Your data is processed to deliver the result you asked for, then handled according to your retention preferences.',
        },
        {
          title: 'Tenant Isolation',
          description:
            'Each customer’s data is logically isolated. Authentication, authorization, and data access controls ensure one customer’s documents, prompts, and AI outputs are never visible to another customer. Workspaces, matters and documents are scoped to the account or firm that created them.',
        },
        {
          title: 'Zero-Retention LLM Agreements',
          description:
            'Where JurisTab uses third-party large language models (for example, for drafting, contract review, or Counsel), those interactions are governed by enterprise agreements that contractually prohibit the provider from retaining, logging, or training on your data. Prompts and responses are processed in memory and discarded.',
        },
        {
          title: 'Provenance & Verifiability',
          description:
            'Every AI-generated output — summaries, extracted clauses, risk findings, drafts — includes provenance indicators so you can trace it back to the source page. This is both a confidentiality control (no fabricated quotes) and a professional-responsibility control (you can always verify before acting).',
        },
      ],
    },
  ],

  faq: {
    label: 'Common Questions',
    title: 'What Clients Most Often Ask',
    items: [
      {
        question: 'Where exactly is our data stored?',
        answer:
          'In the AWS US East (N. Virginia) region — physically inside U.S. data centers operated by AWS. Data for U.S. accounts is stored and processed in the United States.',
      },
      {
        question: 'Can JurisTab staff read our documents?',
        answer:
          'No. Customer data is encrypted at rest and access is restricted to a small group of engineers under strict role-based controls, MFA, and audit logging. Production data is not accessed routinely — only for explicitly authorized support investigations with customer consent or as required by law.',
      },
      {
        question: 'Will our documents be used to train AI?',
        answer:
          'No. We do not train any AI model on customer documents, prompts, or outputs. Third-party LLM providers we use are contractually bound to zero-retention and no-training terms.',
      },
      {
        question: 'What happens to our data if we stop using JurisTab?',
        answer:
          'On account closure, customer data is deleted in line with our retention policy, subject to any legal hold or regulatory obligation. Customers can export their data before closure and may request deletion of personal information under applicable state privacy laws.',
      },
      {
        question: 'Has JurisTab been independently audited?',
        answer:
          'The AWS infrastructure JurisTab is built on is audited annually against ISO 27001, SOC 1/2/3 and PCI DSS, among others. JurisTab’s application-layer security practices align with these standards and are subject to ongoing internal review and external penetration testing.',
      },
      {
        question: 'What happens if there is a data breach?',
        answer:
          'We follow a documented incident response procedure: detect, contain, eradicate, recover, and review. If a breach affects customer data, affected customers will be notified promptly and in accordance with applicable state data-breach notification laws, so firms can meet their own obligations to clients.',
      },
    ],
  },

  cta: {
    title: 'Confidence Begins with Clarity',
    subtitle:
      'We believe trust is earned through transparency. If you have specific data security requirements for your firm or organization, we are happy to walk through them with you.',
    ctaPrimary: 'Contact Our Team',
    ctaPrimaryLink: '/contact',
    ctaSecondary: 'View Privacy Policy',
    ctaSecondaryLink: '/privacy-policy',
  },
};

/* ── Privacy Policy page ── */

const SITE_HOST = new URL(REGION.site.baseUrl).host;

export const PRIVACY_POLICY = {
  hero: {
    label: 'Privacy Policy',
    title: 'Privacy Policy',
    subtitle: 'How we collect, use, and protect your personal information.',
  },

  lastUpdated: '2026',

  sections: [
    {
      title: 'Introduction',
      content:
        `The ${SITE_HOST} website is owned by JurisTab, which is the controller of your personal data.\n\nWe have adopted this Privacy Policy, which describes how we process the information collected by ${SITE_HOST} and the reasons we must collect certain personal data about you. Please read this Privacy Policy before using the ${SITE_HOST} website.\n\nWe take care of your personal data and undertake to guarantee its confidentiality and security.`,
    },
    {
      title: 'Personal Information We Collect',
      content:
        `When you visit ${SITE_HOST}, we automatically collect certain information about your device, including information about your web browser, IP address, time zone, and some of the cookies installed on your device. Additionally, as you browse the Site, we collect information about the individual web pages or products you view, what websites or search terms referred you to the Site, and how you interact with the Site. We refer to this automatically-collected information as "Device Information." Moreover, we may collect the personal data you provide to us (including but not limited to name, address, payment information, etc.) during registration to be able to fulfill the agreement.`,
    },
    {
      title: 'Why Do We Process Your Data?',
      content:
        `Our top priority is customer data security, and, as such, we process only minimal user data, only as much as is absolutely necessary to maintain the website. Information collected automatically is used only to identify potential cases of abuse and establish statistical information regarding website usage. This statistical information is not otherwise aggregated in such a way that it would identify any particular user of the system.\n\nYou can visit the website without telling us who you are or revealing any information by which someone could identify you as a specific individual. If, however, you wish to use some of the website's features, or you wish to receive our newsletter or provide other details by filling out a form, you may provide personal data to us, such as your email, first name, last name, city of residence, organization, and telephone number. You can choose not to provide us with your personal data, but then you may not be able to take advantage of some of the website's features. For example, you won't be able to receive our newsletter or contact us directly from the website. Users who are uncertain about what information is mandatory are welcome to contact us via ${REGION.contact.email}.`,
    },
    {
      title: 'Your Rights',
      content:
        'Depending on where you live — including in California and other states with consumer privacy laws — you may have the following rights related to your personal data:',
      list: [
        'The right to know what personal information we collect and how it is used.',
        'The right of access to your personal information.',
        'The right to correct inaccurate personal information.',
        'The right to delete your personal information.',
        'The right to data portability.',
        'The right to opt out of the sale or sharing of personal information (we do not sell personal information).',
        'The right not to be discriminated against for exercising these rights.',
        'Rights in relation to automated decision-making and profiling.',
      ],
      afterList:
        'If you would like to exercise any of these rights, please contact us through the contact information below. We will verify your request and respond within the time required by applicable law.',
    },
    {
      title: 'Links to Other Websites',
      content:
        'Our website may contain links to other websites that are not owned or controlled by us. Please be aware that we are not responsible for such other websites or third parties\' privacy practices. We encourage you to be aware when you leave our website and read the privacy statements of each website that may collect personal information.',
    },
    {
      title: 'Information Security',
      content:
        'We secure information you provide on computer servers in a controlled, secure environment, protected from unauthorized access, use, or disclosure. We keep reasonable administrative, technical, and physical safeguards to protect against unauthorized access, use, modification, and disclosure of personal data in our control and custody. However, no data transmission over the Internet or wireless network can be guaranteed.',
    },
    {
      title: 'Legal Disclosure',
      content:
        'We will disclose any information we collect, use or receive if required or permitted by law, such as to comply with a subpoena or similar legal process, and when we believe in good faith that disclosure is necessary to protect our rights, protect your safety or the safety of others, investigate fraud, or respond to a government request.',
    },
    {
      title: 'Contact Information',
      content:
        `If you would like to contact us to understand more about this Policy or wish to contact us concerning any matter relating to individual rights and your personal information, you may send an email to ${REGION.contact.email}.`,
    },
  ],
};

/* ── Terms & Conditions page ── */

export const TERMS_AND_CONDITIONS = {
  hero: {
    label: 'Terms & Conditions',
    title: 'Terms & Conditions',
    subtitle: 'The rules and regulations for the use of JurisTab\'s website.',
  },

  lastUpdated: '2026',

  sections: [
    {
      title: 'Welcome',
      content:
        `Welcome to JurisTab!\n\nThese terms and conditions outline the rules and regulations for the use of JurisTab's Website, located at ${REGION.site.baseUrl}.\n\nBy accessing this website, we assume you accept these terms and conditions. Do not continue to use JurisTab if you do not agree to all of the terms and conditions stated on this page.`,
    },
    {
      title: 'Cookies',
      content:
        'The website uses cookies to help personalize your online experience. By accessing JurisTab, you agree to the use of the required cookies.\n\nA cookie is a text file that is placed on your hard disk by a web page server. Cookies cannot be used to run programs or deliver viruses to your computer. Cookies are uniquely assigned to you and can only be read by a web server in the domain that issued the cookie to you.\n\nWe may use cookies to collect, store, and track information for statistical or marketing purposes to operate our website. You have the ability to accept or decline optional cookies. There are some required cookies that are necessary for the operation of our website — for example, the one that remembers which country’s site you chose. These cookies do not require your consent as they always work. Please keep in mind that by accepting required cookies, you also accept third-party cookies, which might be used via third-party provided services if you use such services on our website, for example, a video display window provided by third parties and integrated into our website.',
    },
    {
      title: 'License',
      content:
        'Unless otherwise stated, JurisTab and/or its licensors own the intellectual property rights for all material on JurisTab. All intellectual property rights are reserved. You may access this from JurisTab for your own personal use subject to restrictions set in these terms and conditions.\n\nYou must not:',
      list: [
        'Copy or republish material from JurisTab',
        'Sell, rent, or sublicense material from JurisTab',
        'Reproduce, duplicate or copy material from JurisTab',
        'Redistribute content from JurisTab',
      ],
      afterList:
        'This Agreement shall begin on the date hereof.\n\nParts of this website offer users an opportunity to post and exchange opinions and information in certain areas of the website. JurisTab does not filter, edit, publish or review Comments before their presence on the website. Comments do not reflect the views and opinions of JurisTab, its agents, and/or affiliates. Comments reflect the views and opinions of the person who posts them. To the extent permitted by applicable laws, JurisTab shall not be liable for the Comments or any liability, damages, or expenses caused and/or suffered as a result of any use of and/or posting of and/or appearance of the Comments on this website.\n\nJurisTab reserves the right to monitor all Comments and remove any Comments that can be considered inappropriate, offensive, or in breach of these Terms and Conditions.\n\nYou warrant and represent that:',
      secondList: [
        'You are entitled to post the Comments on our website and have all necessary licenses and consents to do so;',
        'The Comments do not invade any intellectual property right, including without limitation copyright, patent, or trademark of any third party;',
        'The Comments do not contain any defamatory, libelous, offensive, indecent, or otherwise unlawful material, which is an invasion of privacy.',
        'The Comments will not be used to solicit or promote business or custom or present commercial activities or unlawful activity.',
      ],
      afterSecondList:
        'You hereby grant JurisTab a non-exclusive license to use, reproduce, edit and authorize others to use, reproduce and edit any of your Comments in any and all forms, formats, or media.',
    },
    {
      title: 'Hyperlinking to Our Content',
      content:
        'The following organizations may link to our Website without prior written approval:',
      list: [
        'Government agencies;',
        'Search engines;',
        'News organizations;',
        'Online directory distributors may link to our Website in the same manner as they hyperlink to the Websites of other listed businesses; and',
        'System-wide Accredited Businesses except soliciting non-profit organizations, charity shopping malls, and charity fundraising groups which may not hyperlink to our Web site.',
      ],
      afterList:
        'These organizations may link to our home page, to publications, or to other Website information so long as the link: (a) is not in any way deceptive; (b) does not falsely imply sponsorship, endorsement, or approval of the linking party and its products and/or services; and (c) fits within the context of the linking party\'s site.\n\nWe may consider and approve other link requests from the following types of organizations:',
      secondList: [
        'Commonly-known consumer and/or business information sources;',
        'Dot.com community sites;',
        'Associations or other groups representing charities;',
        'Online directory distributors;',
        'Internet portals;',
        'Accounting, law, and consulting firms; and',
        'Educational institutions and trade associations.',
      ],
      afterSecondList:
        'We will approve link requests from these organizations if we decide that: (a) the link would not make us look unfavorably to ourselves or to our accredited businesses; (b) the organization does not have any negative records with us; (c) the benefit to us from the visibility of the hyperlink compensates the absence of JurisTab; and (d) the link is in the context of general resource information.\n\nThese organizations may link to our home page so long as the link: (a) is not in any way deceptive; (b) does not falsely imply sponsorship, endorsement, or approval of the linking party and its products or services; and (c) fits within the context of the linking party\'s site.\n\nIf you are one of the organizations listed above and are interested in linking to our website, you must inform us by sending an email to JurisTab. Please include your name, your organization name, contact information as well as the URL of your site, a list of any URLs from which you intend to link to our Website, and a list of the URLs on our site to which you would like to link. Wait 2-3 weeks for a response.\n\nApproved organizations may hyperlink to our Website as follows:',
      thirdList: [
        'By use of our corporate name; or',
        'By use of the uniform resource locator being linked to; or',
        'Using any other description of our Website being linked to that makes sense within the context and format of content on the linking party\'s site.',
      ],
      afterThirdList:
        'No use of JurisTab\'s logo or other artwork will be allowed for linking absent a trademark license agreement.',
    },
    {
      title: 'Content Liability',
      content:
        'We shall not be held responsible for any content that appears on your Website. You agree to protect and defend us against all claims that are raised on your Website. No link(s) should appear on any Website that may be interpreted as libelous, obscene, or criminal, or which infringes, otherwise violates, or advocates the infringement or other violation of, any third party rights.',
    },
    {
      title: 'Reservation of Rights',
      content:
        'We reserve the right to request that you remove all links or any particular link to our Website. You agree to immediately remove all links to our Website upon request. We also reserve the right to amend these terms and conditions and its linking policy at any time. By continuously linking to our Website, you agree to be bound to and follow these linking terms and conditions.',
    },
    {
      title: 'Removal of Links from Our Website',
      content:
        'If you find any link on our Website that is offensive for any reason, you are free to contact and inform us at any moment. We will consider requests to remove links, but we are not obligated to do so or to respond to you directly.\n\nWe do not ensure that the information on this website is correct. We do not warrant its completeness or accuracy, nor do we promise to ensure that the website remains available or that the material on the website is kept up to date.',
    },
    {
      title: 'Disclaimer',
      content:
        'To the maximum extent permitted by applicable law, we exclude all representations, warranties, and conditions relating to our website and the use of this website. Nothing in this disclaimer will:',
      list: [
        'Limit or exclude our or your liability for death or personal injury;',
        'Limit or exclude our or your liability for fraud or fraudulent misrepresentation;',
        'Limit any of our or your liabilities in any way that is not permitted under applicable law; or',
        'Exclude any of our or your liabilities that may not be excluded under applicable law.',
      ],
      afterList:
        'The limitations and prohibitions of liability set in this Section and elsewhere in this disclaimer: (a) are subject to the preceding paragraph; and (b) govern all liabilities arising under the disclaimer, including liabilities arising in contract, in tort, and for breach of statutory duty.\n\nAs long as the website and the information and services on the website are provided free of charge, we will not be liable for any loss or damage of any nature.',
    },
  ],
};

/* ── Practice Areas page ──
   The three suites are the U.S. workflow catalogue (packs/us: real estate
   s15–s20, family s21–s34, criminal s35–s47). Other practice areas run the
   general services. No statistics or example searches: those described the
   research corpus, which the U.S. does not have. */

const GENERAL_SERVICES_NOTE =
  'Runs on the general workflow services — Contract Review & Redline, Risk & Issues Report, Document Summary, Clause Extraction, Key Dates, Obligation Checklist and Document Matrix Review — with the matter’s documents and Counsel for questions across them.';

export const PRACTICE_AREAS = {
  hero: {
    label: 'Practice Areas',
    title: 'AI-Powered Workflow for Every Practice Area',
    subtitle:
      'Dedicated workflow suites for family law, criminal defense and real estate practice — and document automation that works across every other practice area.',
  },

  intro: {
    label: 'Why Practice-Specific Workflow Matters',
    title: 'Every Practice Area Runs on Different Documents',
    text: 'A family law attorney building a marital estate schedule faces entirely different document work than a defense attorney working through discovery, or a real estate attorney reviewing a title commitment. JurisTab’s suites are built around the documents each matter actually runs on — and the general services cover the rest.',
  },

  areas: [
    {
      id: 'family-law',
      title: 'Family Law',
      shortDesc: 'Divorce, custody, support, property division and prenuptial agreements.',
      icon: 'FaHome',
      colour: '#D9A04A',
      overview:
        'Family law attorneys work from financial disclosures, bank and retirement statements, declarations and settlement drafts — under community property rules in some states and equitable distribution in others. The suite turns the disclosure bundle into a traced marital estate schedule and drafts the documents the matter needs next.',
      stats: [],
      benefits: [
        {
          title: 'Marital Estate Schedule',
          description:
            'Every asset and debt from the disclosures in one schedule, characterized as marital or separate, with each figure linked to the page it came from and every total calculated in code.',
        },
        {
          title: 'Disclosure Cross-Check',
          description:
            'Financial disclosures checked against the bank, brokerage and retirement statements behind them — omissions, mismatched balances and undisclosed accounts flagged for follow-up.',
        },
        {
          title: 'QDRO & Settlement Drafting',
          description:
            'Retirement asset division checked for what a QDRO needs, and a marital settlement agreement and stipulated judgment drafted from the agreed terms.',
        },
      ],
      exampleQuery: '',
      suite: {
        count: '14 services',
        desc: 'Every line of output links to its source page, and figures such as the marital estate are calculated in code.',
        services: [
          'Financial Disclosure Cross-Check', 'Marital Estate Schedule', 'Chronology Builder', 'Declaration / Affidavit Draft',
          'Marital Settlement Agreement & Stipulated Judgment', 'Trial Exhibit Index', 'Subpoena & Discovery Production Review',
          'Domestic Violence & Risk Screening', 'Family Law Correspondence', 'Prenuptial / Postnuptial Agreement Review',
          'Business & Trust Analysis', 'Retirement Asset Division (QDRO) Check', 'Client-Friendly Explanation',
          'Settlement Offer & Fee Analysis',
        ],
      },
    },
    {
      id: 'criminal-defense',
      title: 'Criminal Defense',
      shortDesc: 'Discovery review, suppression issues, plea negotiations and sentencing.',
      icon: 'FaGavel',
      colour: '#D94A4A',
      overview:
        'Defense attorneys work through police reports, body-camera transcripts, interrogation records and witness statements under tight deadlines. The suite reads the discovery, finds the inconsistencies and constitutional issues, and drafts the letters and memoranda the case needs — set to the offense’s jurisdiction, federal or state.',
      stats: [],
      benefits: [
        {
          title: 'Discovery & Evidence Review',
          description:
            'The whole discovery production read and organized — each witness’s account compared, inconsistencies flagged with page references, and gaps that call for a Brady request identified.',
        },
        {
          title: 'Miranda & Suppression Issues',
          description:
            'Custodial interrogation records reviewed for warnings, waivers and invocations, so suppression issues surface early enough to brief.',
        },
        {
          title: 'Plea & Sentencing Work',
          description:
            'Criminal history and sentencing exposure set out, then a plea negotiation letter or a sentencing memorandum in mitigation drafted from the record.',
        },
      ],
      exampleQuery: '',
      suite: {
        count: '13 services',
        desc: 'Each run is set to the jurisdiction of the offense — a federal offense, any of the 50 states, or the District of Columbia.',
        services: [
          'Discovery & Evidence Review', 'Charge Analysis', 'Witness Inconsistency Check', 'Custodial Interrogation / Miranda Review',
          'Motions in Limine & Evidentiary Objections', 'Criminal History & Sentencing Exposure', 'Criminal Chronology',
          'Pretrial Release / Bail Motion', 'Sentencing Memorandum (Mitigation)', 'Discovery / Brady Request Letter',
          'Plea Negotiation / Charge-Reduction Letter', 'DUI/DWI & Traffic Assessment', 'Client-Friendly Explanation (Criminal)',
        ],
      },
    },
    {
      id: 'real-estate',
      title: 'Real Estate Law',
      shortDesc: 'Purchase agreements, contingencies, title and closings.',
      icon: 'FaBuilding',
      colour: '#4AD98A',
      overview:
        'Real estate attorneys move a transaction from purchase agreement to closing through contingencies, seller disclosures and the title commitment. The suite reviews the agreement and disclosures together, reports on Schedule B exceptions, and drafts the addenda the deal needs.',
      stats: [],
      benefits: [
        {
          title: 'Purchase Agreement & Disclosure Review',
          description:
            'The purchase and sale agreement reviewed against the seller’s disclosures — deadlines, contingencies, remedies and anything the disclosures contradict, each with a page reference.',
        },
        {
          title: 'Title Commitment & Exceptions Memo',
          description:
            'Schedule B exceptions and requirements in the title commitment summarized for the client and the closing — easements, liens and restrictions that need attention before closing.',
        },
        {
          title: 'Addenda & Contingencies',
          description:
            'Inspection, financing and appraisal contingencies and other addenda drafted from the deal terms, ready to negotiate.',
        },
      ],
      exampleQuery: '',
      suite: {
        count: '6 services',
        desc: 'Purchase agreements reviewed with the seller’s disclosures, title reported on, addenda drafted, and the answer explained for the client.',
        services: [
          'Purchase Agreement & Disclosure Review', 'Purchase Agreement Addenda & Contingencies Draft',
          'Seller Disclosure Statement Builder', 'Title Commitment & Exceptions Memo',
          'Real Property Law Research', 'Plain-Language Explainer',
        ],
      },
    },
    {
      id: 'corporate-commercial',
      title: 'Corporate & Commercial Law',
      shortDesc: 'Commercial contracts, M&A due diligence and corporate governance.',
      icon: 'FaBriefcase',
      colour: '#4A90D9',
      overview:
        'Corporate attorneys live in contracts: supply and services agreements, operating agreements, NDAs and data-room documents. Contract Review & Redline, Clause Comparison and Document Matrix Review take on the volume while you keep the judgment calls.',
      stats: [],
      benefits: [
        {
          title: 'Contract Review & Redline',
          description: 'Clause-by-clause review against your position, with a tracked-changes redline back in Word.',
        },
        {
          title: 'Due Diligence at Volume',
          description: 'Document Matrix Review puts every data-room document against your diligence questions — change of control, assignment, exclusivity — with page citations, exported to Excel.',
        },
        {
          title: 'Obligations & Key Dates',
          description: 'Obligation checklists and key dates pulled from executed agreements so renewals and notice periods are never missed.',
        },
      ],
      exampleQuery: '',
    },
    {
      id: 'employment-labor',
      title: 'Employment & Labor Law',
      shortDesc: 'Employment agreements, handbooks, separation agreements and EEOC charges.',
      icon: 'FaUsers',
      colour: '#9B59B6',
      overview: GENERAL_SERVICES_NOTE,
      stats: [],
      benefits: [
        {
          title: 'Agreement & Handbook Review',
          description: 'Employment, non-compete and separation agreements reviewed for risk and enforceability issues, with a redline back.',
        },
        {
          title: 'Charge & Claim Chronologies',
          description: 'Key dates and events pulled from personnel files and correspondence to support an EEOC charge response or a demand letter.',
        },
        {
          title: 'Policy Comparison',
          description: 'Clause Comparison shows exactly what changed between handbook or agreement versions.',
        },
      ],
      exampleQuery: '',
    },
    {
      id: 'wills-trusts-estates',
      title: 'Wills, Trusts & Estates',
      shortDesc: 'Estate planning documents, trust administration and probate.',
      icon: 'FaScroll',
      colour: '#7D6608',
      overview: GENERAL_SERVICES_NOTE,
      stats: [],
      benefits: [
        {
          title: 'Instrument Summaries',
          description: 'Wills and trust instruments summarized — fiduciaries, beneficiaries, distribution provisions and powers — with page references.',
        },
        {
          title: 'Administration Checklists',
          description: 'Fiduciary obligations and deadlines pulled into a checklist for the administration.',
        },
        {
          title: 'Plain-Language Explanations',
          description: 'Complex instruments explained for clients and family members in plain English.',
        },
      ],
      exampleQuery: '',
    },
    {
      id: 'personal-injury',
      title: 'Personal Injury & Insurance Law',
      shortDesc: 'Medical records, insurance policies, demand letters and claims.',
      icon: 'FaHeartbeat',
      colour: '#E74C3C',
      overview: GENERAL_SERVICES_NOTE,
      stats: [],
      benefits: [
        {
          title: 'Medical Record Chronologies',
          description: 'Treatment timelines built from medical records, with every entry linked to its page.',
        },
        {
          title: 'Policy Review',
          description: 'Insurance policies reviewed for coverage, exclusions and notice requirements.',
        },
        {
          title: 'Demand Drafting',
          description: 'First drafts of demand letters on your letterhead, built from the documents in the matter.',
        },
      ],
      exampleQuery: '',
    },
    {
      id: 'immigration',
      title: 'Immigration Law',
      shortDesc: 'Petitions, supporting evidence and client correspondence.',
      icon: 'FaGlobe',
      colour: '#E67E22',
      overview: GENERAL_SERVICES_NOTE,
      stats: [],
      benefits: [
        {
          title: 'Evidence Organization',
          description: 'Supporting documents organized, indexed and summarized for the petition.',
        },
        {
          title: 'Key Dates',
          description: 'Filing windows, priority dates and expirations extracted from the file.',
        },
        {
          title: 'Client Explanations',
          description: 'Notices and requests for evidence explained to clients in plain English.',
        },
      ],
      exampleQuery: '',
    },
  ],

  howItWorks: {
    label: 'How JurisTab Works for You',
    title: 'From Documents to Work Product in Minutes',
    steps: [
      {
        title: 'Open the Matter',
        description: 'Upload the documents to the matter — or use the ones already in the file. Uploading, OCR and organizing are always free.',
      },
      {
        title: 'Pick a Service',
        description: 'Choose a general service or one from your practice area’s suite. You see the exact cost before anything runs.',
      },
      {
        title: 'Review Cited Output',
        description: 'Every finding links back to the page it came from, so you can verify before you rely on it.',
      },
      {
        title: 'Export and Move On',
        description: 'Export to Word or Excel on your letterhead, and keep working in the same matter file.',
      },
    ],
  },

  suiteLabel: 'Dedicated Workflow Services',
  suiteLink: '/workflow-features',
  suiteLinkText: 'See the suite on the Workflow page →',

  cta: {
    title: 'Put Your Practice Area’s Documents to Work',
    subtitle:
      'Start free with 1,000 trial credits and run the services your practice actually uses on your own files.',
    ctaPrimary: 'Start Free Trial',
    ctaPrimaryLink: APP_URL,
    ctaSecondary: 'Contact Us',
    ctaSecondaryLink: '/contact',
  },
};

/* ── Footer ── */

export const FOOTER = {
  tagline: SITE.tagline,
  productsHeading: 'Products',
  products: [
    { label: 'JurisTab', href: APP_URL, external: true },
  ],
  companyHeading: 'Company',
  companyLinks: [
    { label: 'About Us', href: '/about' },
    { label: 'Case Management', href: '/case-management' },
    { label: 'Workflow', href: '/workflow-features' },
    { label: 'Practice Areas', href: '/practice-areas' },
    { label: 'Pricing', href: '/pricing' },
    { label: 'Blogs', href: '/blogs' },
    { label: 'Contact', href: '/contact' },
  ],
  supportHeading: 'Support',
  supportLinks: [
    { label: 'Privacy Policy', href: '/privacy-policy' },
    { label: 'Terms & Conditions', href: '/terms-and-conditions' },
  ],
  newsletter: {
    heading: 'Stay Updated',
    text: 'Subscribe to our newsletter for the latest legal-tech insights and product updates.',
    placeholder: 'Enter your email',
    button: 'Subscribe',
  },
  bottomLinks: [
    { label: 'Privacy', href: '/privacy-policy' },
    { label: 'Terms', href: '/terms-and-conditions' },
    { label: 'Contact', href: '/contact' },
  ],
  marketSwitcherLabel: 'Choose your country',
};

/* ── Market notice ── */

/* The edge sends visitors from the U.S. here from the Australian site with
   ?from=au; they get one line offering the way back (which sets jt_site=au). */
export const MARKET_NOTICE: MarketNotice | null = {
  param: 'from',
  value: 'au',
  target: 'au',
  text: 'You’ve been sent to our US site.',
  linkPrefix: 'Looking for Australia?',
  linkLabel: 'juristab.com',
  dismissLabel: 'Dismiss',
};

/* ── Tools & Calculators ── */

/* The two modules JurisTab sells in the U.S. The backend models a third
   (legal research); the calculator always sends this list, so it is never
   counted here. */
export const CALC_MODULES: { key: string; label: string; hint: string; info: string }[] = [
  {
    key: 'case_management',
    label: 'Case Management',
    hint: 'Intake, conflicts, matters, calendaring, time, billing, trust, signing',
    info: 'Running the practice: taking on a client, opening and organizing the matter file, calendaring deadlines, capturing time and getting the bill out. Turn it off if you will keep using your existing practice-management system.',
  },
  {
    key: 'workflow',
    label: 'Workflow Automation',
    hint: '45 agentic services — review, redline, extract, draft',
    info: 'The document work: reading a contract for risk, pulling out clauses, dates and obligations, comparing versions, drafting and organizing bundles. This is the biggest single slice of time for most firms.',
  },
];

export const CALC_PLANS: { key: string; label: string; info: string }[] = [
  { key: 'auto', label: 'Cheapest for me', info: 'We price every plan at the volumes you entered and cost the report on whichever comes out cheapest. This is the default and usually the right answer.' },
  { key: 'pay_as_you_go', label: 'Pay as you go', info: 'No subscription at all — every page and new contact is paid for in credits as you use it. Cheapest for light or occasional use.' },
  { key: 'pro_case_management', label: 'Case Management', info: `${money(39)} per user per month. Unlimited case management — matters, contacts, calendars, time, billing and trust. Document pages are still metered.` },
  { key: 'pro_workflow', label: 'Workflow', info: `${money(119)} per user per month, including 1,200 processed pages each month. Pages past that are charged at the plan rate.` },
  { key: 'pro_plus', label: 'Pro+', info: `${money(179)} per user per month. Unlimited case management plus 3,000 processed pages a month, with 25% rollover. The usual answer for a team using both modules.` },
];

export const CALC_SCENARIOS: { key: string; label: string; info: string; default?: boolean }[] = [
  { key: 'conservative', label: 'Conservative', info: 'Assumes each module gives back 70% of the time our model says it should. Use this when you want the number to survive a skeptical CFO.' },
  { key: 'expected', label: 'Expected', info: 'The model as measured — the middle case, and where the calculator starts.', default: true },
  { key: 'optimistic', label: 'Optimistic', info: 'Assumes 25% more time back than the model says — a well-run rollout with a team that adopts the tools properly.' },
];

export const CALC_ADOPTIONS: { key: 3 | 6 | 12; label: string; info: string; default?: boolean }[] = [
  { key: 3, label: '3 mo', info: 'A fast rollout: usage climbs to full over the first 3 months, so Year 1 captures 87.5% of the full benefit. Years 2 and 3 get all of it.' },
  { key: 6, label: '6 mo', info: 'A typical rollout — usage climbs to full over 6 months, so Year 1 captures 75% of the full benefit and Years 2 and 3 get all of it. This is where the calculator starts.', default: true },
  { key: 12, label: '12 mo', info: 'A cautious rollout across a large or busy team: usage takes the whole first year to reach full, so Year 1 captures 50% of the benefit. Years 2 and 3 get all of it.' },
];

export const CALC_BUFFERS: { key: number; label: string; info: string; default?: boolean }[] = [
  { key: 0, label: 'None', info: 'Show the model exactly as it computes, with no safety margin. Useful to see the raw arithmetic; not what we would put in front of a board.' },
  { key: 0.1, label: '10%', info: 'A light safety margin: you are shown 90% of the savings the model works out.' },
  { key: 0.15, label: '15%', info: 'The default. You are shown 85% of the savings the model works out — the other 15% is held back for everything a model cannot see: the matters that do not compress, the weeks nobody logs in, the assumption that turns out generous.', default: true },
  { key: 0.25, label: '25%', info: 'A heavy safety margin: you are shown 75% of the savings. Pair it with the conservative scenario for a genuine worst case.' },
];

export const CALCULATORS_HERO = {
  label: 'Tools & Calculators',
  title: 'Quantify your return on JurisTab',
  subtitle:
    'JurisTab spans two modules — case management and workflow automation — and each gives back a different slice of the week. These calculators show where you stand today, price the regained capacity against the plan you would actually be on, and tell you which plan that is.',
  modulesInfo:
    'JurisTab is two products in one. Each gives back a different slice of the week, so only the ones you switch on are counted towards your saving. Both are on by default.',
  modulesHint:
    'Each module gives back a different slice of the week, so only the ones you turn on are counted. Default: both.',
};

/* Never asked in the U.S.: research is not sold here, so the backend's
   profession defaults for it must not be priced in. */
const NO_RESEARCH_USAGE = {
  case_unlocks_per_month: 0,
  legislation_unlocks_per_month: 0,
  jurisbot_messages_per_month: 0,
};

const PRACTICE_AREAS_FIELD_INFO = `How many practice areas you will subscribe to. The quoted price covers one; each extra area adds 33% of it on Workflow and Pro+ (Pro+ at 3 areas is about ${money(297)} per user per month), and 33% on metered usage on Pay as you go. Case Management is one price however many areas you run.`;

const CURRENCY_FIELD_INFO =
  'The currency your own figures are in, and the one results are shown in. JurisTab is sold in U.S. dollars here; other currencies convert the published USD price at an indicative rate, and you are still billed in USD.';

const COST_ROWS = (who: string, group = 'What JurisTab costs'): CalcRow[] => [
  {
    key: 'subscription_cost',
    label: 'JurisTab Subscription',
    format: 'money',
    group,
    info: `A seat for each of your ${who}, on the plan shown above, at the annual price — pay for ten months and get twelve. This is the fixed part of the bill.`,
  },
  {
    key: 'usage_cost',
    label: 'JurisTab Metered Usage',
    format: 'money',
    group,
    info: 'The variable part of the bill: document pages beyond what your plan includes, plus new contact records if your plan does not make them unlimited. Uploading, OCR, matters, tasks and exports are free on every plan.',
  },
  {
    key: 'cost_of_juristab',
    label: 'Total Cost of JurisTab',
    format: 'money',
    group,
    info: 'Subscription plus metered usage — the whole JurisTab bill for the year, with nothing held back. Today it is zero, because you are not paying it yet.',
  },
];

export const CALCULATORS: Record<string, CalcProfession> = {
  'in-house': {
    slug: 'in-house',
    service: 'in-house',
    navLabel: 'In-House Counsel',
    badge: 'In-House / Corporate Legal',
    title: 'In-House Legal ROI Calculator',
    subtitle:
      'In-house legal departments don’t bill by the hour, so value shows up as freed capacity (deferring the next hire) and outside-counsel spend brought back in-house. Every figure is shown against where your team stands today.',
    formTitle: 'Your Team',
    fields: [
      {
        name: 'num_lawyers',
        label: 'Number of Attorneys',
        type: 'number',
        default: 20,
        min: 1,
        step: 1,
        info: 'How many attorneys sit in your legal department. Count anyone who would use JurisTab — paralegals and legal ops included — since they all need a seat and they all get time back.',
      },
      {
        name: 'practice_areas',
        label: 'Practice Areas',
        type: 'number',
        default: 1,
        min: 1,
        step: 1,
        info: PRACTICE_AREAS_FIELD_INFO,
      },
      {
        name: 'business_growth_percent',
        label: 'Business Growth (% per year)',
        type: 'number',
        default: 4,
        min: 0,
        step: 0.5,
        hint: 'Year 1 is today’s volume; Years 2 and 3 grow by this much each.',
        info: 'How much your legal workload grows each year — more matters, more contracts, a bigger outside-counsel bill. Year 1 is today’s volume; Years 2 and 3 grow by this much each, so there is more work for JurisTab to speed up and more outside work to bring in-house. Your JurisTab subscription does not grow with it. U.S. legal services have grown around 4% a year; set 0 to model no growth.',
      },
      {
        name: 'avg_loaded_salary',
        label: 'Average Attorney Salary',
        type: 'currency',
        default: 185000,
        min: 0,
        step: 1000,
        hint: 'Annual base salary; we apply a loaded-cost multiplier for benefits & overhead.',
        info: 'The average base salary across the team, before benefits, bonuses or overhead. We multiply it by 1.3 to get the true cost of employing someone — benefits, payroll taxes, retirement contributions and overhead — which is what an hour of their time is actually worth to the business.',
      },
      {
        name: 'outside_counsel_spend',
        label: 'Annual Outside Counsel Spend',
        type: 'currency',
        default: 1000000,
        min: 0,
        step: 10000,
        info: 'What you pay outside law firms in a year, across all panel firms and one-off engagements. With more internal capacity, a slice of this work comes back in-house — that slice is a direct cash saving.',
      },
      {
        name: 'currency',
        label: 'Currency',
        type: 'currency-select',
        default: REGION.currency,
        info: CURRENCY_FIELD_INFO,
      },
    ],
    usageFields: [
      {
        name: 'pages_per_month',
        label: 'Pages Processed / Attorney / Month',
        type: 'number',
        default: 300,
        min: 0,
        step: 10,
        hint: 'Pages a workflow service actually reads. Upload, OCR and export are never counted.',
        info: 'Pages an AI service actually reads for you in a month — a contract you send for risk review, a lease you pull key dates out of. Just storing, uploading, OCR-ing or exporting a document costs nothing, so those pages do not count here.',
      },
      {
        name: 'new_contacts_per_month',
        label: 'New Contact Records / Attorney / Month',
        type: 'number',
        default: 3,
        min: 0,
        step: 1,
        info: 'New people or organizations added to the contact book each month. Only creating a brand-new record is metered — editing, linking and matter work are all free.',
      },
    ],
    fixedInputs: NO_RESEARCH_USAGE,
    team: {
      headcountField: 'num_lawyers',
      rateField: 'avg_loaded_salary',
      rateLabel: 'Annual salary',
      rateKind: 'salary',
      roles: ['General counsel', 'Deputy general counsel', 'Corporate counsel', 'Paralegal', 'Legal ops'],
      info: 'Enter the team as it really is — full-timers and part-timers, each on their own salary. Everyone who uses JurisTab needs a seat, so every person is priced; the time given back and what an hour is worth follow how many days each person actually works. For a part-timer, enter what you actually pay them.',
    },
    headlineKey: 'annual_roi_multiple',
    headlineLabel: 'Annual Return on Investment',
    headlineInfo:
      'For every dollar you spend on JurisTab in Year 1, this is the value that comes back — freed capacity plus outside-counsel savings, divided by the total bill. 3× means three dollars back for every one spent.',
    baselineLabel: 'Today',
    baselineInfo:
      'Where your legal department stands right now, before JurisTab: what the team costs, what you send out to outside firms, and how much of the year goes into work the modules you selected can help with. Every later column is measured against this one.',
    supportMetrics: [
      {
        key: 'year_1_net_benefit',
        label: 'Year 1 Net Benefit',
        format: 'money',
        info: 'Value created in the first year — freed capacity plus outside-counsel savings — after the whole JurisTab bill has been taken off.',
      },
      {
        key: 'fte_equivalent_year_1',
        label: 'FTE Capacity Regained',
        format: 'number',
        info: 'The hours given back, expressed as full-time attorneys. 2.5 means you get the working year of two and a half people back without hiring anyone.',
      },
      {
        key: 'payback_period_months',
        label: 'Payback Period',
        format: 'months',
        info: 'How long it takes for the value created to cover the annual cost of JurisTab. Under twelve months means it pays for itself inside the first year.',
      },
    ],
    rows: [
      {
        key: 'hours_on_addressable_work',
        label: 'Hours on Work JurisTab Touches',
        format: 'hours',
        group: 'Where the team’s time goes',
        info: 'Hours a year your team currently spends on the work the modules you selected can help with — file admin and document work. This is the pool the saving comes out of, not your whole working year.',
      },
      {
        key: 'time_regained_hours',
        label: 'Time Regained (Hours)',
        format: 'hours',
        group: 'Where the team’s time goes',
        info: 'Hours a year handed back to the team, after the safety margin and the adoption ramp. Today it is zero, because none of this work is automated yet.',
      },
      {
        key: 'fte_equivalent',
        label: 'FTE-Equivalent Capacity',
        format: 'number',
        group: 'Where the team’s time goes',
        info: 'Those regained hours expressed as full-time attorneys — the headcount you would otherwise have to hire to get the same capacity.',
      },
      {
        key: 'internal_team_cost',
        label: 'In-House Team Cost',
        format: 'money',
        group: 'Cash out the door',
        info: 'What your attorneys cost the business a year: salaries multiplied by 1.3 for benefits, payroll taxes, retirement contributions and overhead. This does not change — the model never assumes anyone is let go.',
      },
      {
        key: 'outside_counsel_spend',
        label: 'Outside Counsel Spend',
        format: 'money',
        group: 'Cash out the door',
        info: 'Your annual bill from outside law firms. It falls each year as freed capacity lets the team keep more of that work in-house.',
      },
      ...COST_ROWS('attorneys', 'Cash out the door'),
      {
        key: 'total_run_rate',
        label: 'Total Legal Run-Rate',
        format: 'money',
        group: 'Cash out the door',
        info: 'Real money leaving the business each year: your team, your outside firms and JurisTab added together. Freed capacity is deliberately left out of this line — it is value, not a check anyone stops writing.',
      },
      {
        key: 'freed_capacity_value',
        label: 'Freed Capacity Value',
        format: 'money',
        group: 'Value created',
        info: 'The regained hours priced at what your team actually costs per hour. It is the value of the hire you do not have to make — real, but it shows up as work absorbed rather than as cash in the bank.',
      },
      {
        key: 'outside_counsel_savings',
        label: 'Outside Counsel Savings',
        format: 'money',
        group: 'Value created',
        info: 'Outside legal spend you stop paying because the freed capacity lets you handle that work internally. Unlike freed capacity, this one is cash you genuinely keep.',
      },
      {
        key: 'potential_additional_profit',
        label: 'Net Annual Benefit',
        format: 'money',
        group: 'The bottom line',
        emphasis: true,
        info: 'Freed capacity plus outside-counsel savings, minus everything JurisTab costs. The single number to take to a budget meeting.',
      },
      {
        key: 'profit_increase_percent',
        label: 'Increase vs Today',
        format: 'percent',
        group: 'The bottom line',
        info: 'Net Annual Benefit as a share of what the legal department costs today — the in-house team’s loaded cost plus outside-counsel spend. In-house teams have no profit line, so this is the saving measured against your current legal budget.',
      },
    ],
  },

  'private-practice': {
    slug: 'private-practice',
    service: 'private-practice',
    navLabel: 'Law Firms',
    badge: 'Private Practice / Law Firm',
    title: 'Law Firm ROI Calculator',
    subtitle:
      'For timekeepers, regained hours become incremental billable capacity — valued at your rate and discounted by realization and collection, the two classic law-firm leakage metrics. Shown against the fees you already collect today.',
    formTitle: 'Your Firm',
    fields: [
      {
        name: 'num_lawyers',
        label: 'Number of Attorneys',
        type: 'number',
        default: 50,
        min: 1,
        step: 1,
        info: 'How many timekeepers you have — everyone who records billable time and would use JurisTab. Staff who never bill are best left out.',
      },
      {
        name: 'practice_areas',
        label: 'Practice Areas',
        type: 'number',
        default: 1,
        min: 1,
        step: 1,
        info: PRACTICE_AREAS_FIELD_INFO,
      },
      {
        name: 'business_growth_percent',
        label: 'Business Growth (% per year)',
        type: 'number',
        default: 4,
        min: 0,
        step: 0.5,
        hint: 'Year 1 is today’s volume; Years 2 and 3 grow by this much each.',
        info: 'How much the firm’s work grows each year — more matters, more documents, more fees. Year 1 is today’s volume; Years 2 and 3 grow by this much each, so there is more work for JurisTab to speed up. Your JurisTab subscription does not grow with it. U.S. legal services have grown around 4% a year; set 0 to model no growth.',
      },
      {
        name: 'hourly_rate',
        label: 'Average Hourly Rate',
        type: 'currency',
        default: 350,
        min: 0,
        step: 10,
        hint: 'Your average billable rate per hour.',
        info: 'The blended rate you bill across the firm — partners, associates and paralegals averaged together, not the partner rate. Every regained hour is valued at this.',
      },
      {
        name: 'currency',
        label: 'Currency',
        type: 'currency-select',
        default: REGION.currency,
        info: CURRENCY_FIELD_INFO,
      },
    ],
    usageFields: [
      {
        name: 'pages_per_month',
        label: 'Pages Processed / Attorney / Month',
        type: 'number',
        default: 400,
        min: 0,
        step: 10,
        hint: 'Pages a workflow service actually reads. Upload, OCR and export are never counted.',
        info: 'Pages an AI service actually reads for you in a month — a contract you send for risk review, a production you have summarized. Just storing, uploading, OCR-ing or exporting a document costs nothing, so those pages do not count here.',
      },
      {
        name: 'new_contacts_per_month',
        label: 'New Contact Records / Attorney / Month',
        type: 'number',
        default: 5,
        min: 0,
        step: 1,
        info: 'New clients and other parties added to the contact book each month. Only creating a brand-new record is metered — editing, linking and matter work are all free.',
      },
    ],
    fixedInputs: NO_RESEARCH_USAGE,
    team: {
      headcountField: 'num_lawyers',
      rateField: 'hourly_rate',
      rateLabel: 'Hourly rate',
      rateKind: 'hourly',
      roles: ['Partner', 'Of counsel', 'Associate', 'Paralegal', 'Legal assistant'],
      info: 'Enter the firm as it really is — partners, associates and paralegals, full- or part-time, each at their own billing rate. Everyone who uses JurisTab needs a seat, so every person is priced; the time given back follows how many days each person actually works, and each hour is valued at that person’s rate. Put 0 for anyone whose time is not billed.',
    },
    headlineKey: 'annual_roi_multiple',
    headlineLabel: 'Annual Return on Investment',
    headlineInfo:
      'For every dollar you spend on JurisTab in Year 1, this is the fee value that comes back. 3× means three dollars of collected fees for every one spent.',
    baselineLabel: 'Today',
    baselineInfo:
      'Where your firm stands right now, before JurisTab: the hours currently going into work the selected modules can help with, and the fees those hours actually collect. Every later column is measured against this one.',
    supportMetrics: [
      {
        key: 'year_1_additional_profit',
        label: 'Year 1 Additional Profit',
        format: 'money',
        info: 'Extra fees collected in the first year from the freed capacity, after the whole JurisTab bill has been taken off.',
      },
      {
        key: 'three_year_additional_profit',
        label: '3-Year Additional Profit',
        format: 'money',
        info: 'The same figure added up across all three years, as the team climbs the adoption curve and the benefit reaches full strength.',
      },
      {
        key: 'payback_period_months',
        label: 'Payback Period',
        format: 'months',
        info: 'How long it takes for the extra fees to cover the annual cost of JurisTab. Under twelve months means it pays for itself inside the first year.',
      },
    ],
    rows: [
      {
        key: 'hours_on_addressable_work',
        label: 'Hours on Work JurisTab Touches',
        format: 'hours',
        group: 'Where the firm’s time goes',
        info: 'Hours a year your timekeepers currently spend on the work the modules you selected can help with — file admin and document work. This is the pool the saving comes out of, not the whole working year.',
      },
      {
        key: 'time_regained_hours',
        label: 'Time Regained (Hours)',
        format: 'hours',
        group: 'Where the firm’s time goes',
        info: 'Hours a year handed back to timekeepers, after the safety margin and the adoption ramp. Today it is zero, because none of this work is automated yet.',
      },
      {
        key: 'billable_hours_converted',
        label: 'Hours Converted to Billable Work',
        format: 'hours',
        group: 'Where the firm’s time goes',
        info: 'Only the share of freed hours you can actually fill with billable work. We assume half — freeing time is not the same as selling it, and a firm that frees a fifth of its year cannot usually bill a fifth more.',
      },
      {
        key: 'fees_collected',
        label: 'Fees Collected',
        format: 'money',
        group: 'Fees',
        info:
          'What clients actually pay the firm in a year — less than what attorneys bill:\n' +
          `• Hours worked × hourly rate = value of time recorded (e.g. 4 × 1,800 × ${money(350)} = ${money(2520000)})\n` +
          `• × 85% realization: some recorded time is written off or discounted and never invoiced (= ${money(2142000)} invoiced)\n` +
          `• × 90% collection: some invoices are never paid — bad debt, disputes (= ${money(1927800)} collected)\n\n` +
          'It assumes each full-time attorney works a full 1,800-hour year, so it is a capacity figure, not your books. Each later year grows by your Business Growth rate and adds that year’s Net Incremental Billable Capacity.',
      },
      {
        key: 'net_incremental_billable_capacity',
        label: 'Net Incremental Billable Capacity',
        format: 'money',
        group: 'Fees',
        info: 'The extra fees on their own: hours regained × 50% that turn into billable work × your hourly rate × 85% realization × 90% collection. Year 1 is lower while the team ramps up (75% of the full saving with the default 6-month rollout; the team is at full use from then on). Years 2 and 3 rise only with your Business Growth rate — at 0% they are the same.',
      },
      ...COST_ROWS('timekeepers'),
      {
        key: 'potential_additional_profit',
        label: 'Potential Additional Profit',
        format: 'money',
        group: 'The bottom line',
        emphasis: true,
        info: 'The extra fees collected minus everything JurisTab costs. The single number to take to a partners’ meeting.',
      },
      {
        key: 'profit_increase_percent',
        label: 'Profit Increase (% of Today’s Fees)',
        format: 'percent',
        group: 'The bottom line',
        info: 'Potential Additional Profit as a share of today’s Fees Collected. The calculator does not know your current profit, so this is measured against revenue: 5% means extra profit equal to 5% of what the firm collects today. Measured against profit alone, the increase would be several times larger.',
      },
    ],
  },

  'real-estate': {
    slug: 'real-estate',
    service: 'conveyancer',
    navLabel: 'Real Estate Closings',
    badge: 'Real Estate Closing Practice',
    title: 'Real Estate Closing ROI Calculator',
    subtitle:
      'Residential closings are usually billed at a flat fee per transaction, so value is about throughput: faster purchase agreement, title and file work frees capacity for additional closings at the same headcount. Shown against the closings you already handle today.',
    formTitle: 'Your Practice',
    fields: [
      {
        name: 'num_conveyancers',
        label: 'Number of Attorneys & Closers',
        type: 'number',
        default: 8,
        min: 1,
        step: 1,
        info: 'How many people run transactions end to end — attorneys and the paralegals or closers who carry their own files. Everyone who would need a seat.',
      },
      {
        name: 'practice_areas',
        label: 'Practice Areas',
        type: 'number',
        default: 1,
        min: 1,
        step: 1,
        info: PRACTICE_AREAS_FIELD_INFO,
      },
      {
        name: 'business_growth_percent',
        label: 'Business Growth (% per year)',
        type: 'number',
        default: 4,
        min: 0,
        step: 0.5,
        hint: 'Year 1 is today’s volume; Years 2 and 3 grow by this much each.',
        info: 'How much your transaction volume grows each year. Year 1 is today’s volume; Years 2 and 3 grow by this much each, so there are more closings for JurisTab to speed up. Your JurisTab subscription does not grow with it. Set 0 to model no growth.',
      },
      {
        name: 'matters_per_month',
        label: 'Closings per Person / Month',
        type: 'number',
        default: 18,
        min: 1,
        step: 1,
        info: 'Transactions one person closes in an average month. Count each transaction once, whichever side you represent.',
      },
      {
        name: 'fee_per_matter',
        label: 'Average Fee per Closing',
        type: 'currency',
        default: 1500,
        min: 0,
        step: 50,
        info: 'Your professional fee for a standard closing, excluding title premiums, recording fees and other costs you pass straight through.',
      },
      {
        name: 'hours_per_matter',
        label: 'Hours per Closing',
        type: 'number',
        default: 6,
        min: 1,
        step: 1,
        info: 'Hands-on hours a transaction takes from contract to closing. It is what sets your ceiling: cut the hours and the same team can close more.',
      },
      {
        name: 'currency',
        label: 'Currency',
        type: 'currency-select',
        default: REGION.currency,
        info: CURRENCY_FIELD_INFO,
      },
    ],
    /* Pages and new contacts are derived from transaction volume by the
       backend, so nothing is asked here. */
    usageFields: [],
    fixedInputs: NO_RESEARCH_USAGE,
    team: {
      headcountField: 'num_conveyancers',
      roles: ['Real estate attorney', 'Paralegal', 'Closer'],
      info: 'Enter the team as it really is — full- and part-time. Everyone who uses JurisTab needs a seat, so every person is priced; transaction volume and time given back follow how many days each person actually works.',
    },
    headlineKey: 'annual_roi_multiple',
    headlineLabel: 'Annual Return on Investment',
    headlineInfo:
      'For every dollar you spend on JurisTab in Year 1, this is the fee contribution that comes back from the extra closings. 3× means three dollars back for every one spent.',
    baselineLabel: 'Today',
    baselineInfo:
      'Where your practice stands right now, before JurisTab: the closings you complete in a year, the hours going into the parts of them the selected modules can help with, and what those transactions contribute. Every later column is measured against this one.',
    supportMetrics: [
      {
        key: 'year_1_additional_profit',
        label: 'Year 1 Additional Profit',
        format: 'money',
        info: 'Contribution from the extra closings in the first year, after the whole JurisTab bill has been taken off.',
      },
      {
        key: 'additional_matters_year_1',
        label: 'Extra Closings / Year',
        format: 'number',
        info: 'How many additional transactions the same team can close in Year 1 with the time the tools give back.',
      },
      {
        key: 'payback_period_months',
        label: 'Payback Period',
        format: 'months',
        info: 'How long it takes for the extra contribution to cover the annual cost of JurisTab. Under twelve months means it pays for itself inside the first year.',
      },
    ],
    rows: [
      {
        key: 'hours_on_addressable_work',
        label: 'Hours on Work JurisTab Touches',
        format: 'hours',
        group: 'Where the practice’s time goes',
        info: 'Hours a year currently going into the parts of a transaction the modules you selected can help with — the purchase agreement and disclosure review, title commitment summaries, and the file itself.',
      },
      {
        key: 'time_regained_hours',
        label: 'Time Regained (Hours)',
        format: 'hours',
        group: 'Where the practice’s time goes',
        info: 'Hours a year handed back across all your transactions, after the safety margin and the adoption ramp. Today it is zero, because none of this work is automated yet.',
      },
      {
        key: 'annual_matters',
        label: 'Closings per Year',
        format: 'number',
        group: 'Throughput',
        info: 'Transactions closed in a year. "Today" is your current throughput; the later columns add the extra closings the freed time makes room for, at the same headcount.',
      },
      {
        key: 'additional_matter_capacity',
        label: 'Additional Closing Capacity',
        format: 'number',
        group: 'Throughput',
        info: 'The extra closings on their own. We only count half the freed time as new transactions — the work has to exist to fill it, and a slow market will not hand you more files just because you have room.',
      },
      {
        key: 'annual_contribution',
        label: 'Fee Contribution per Year',
        format: 'money',
        group: 'Value created',
        info: 'Fee income from those closings after the variable cost of handling them — the money left over to cover the practice and its profit. "Today" is your current position.',
      },
      {
        key: 'additional_revenue',
        label: 'Additional Contribution',
        format: 'money',
        group: 'Value created',
        info: 'The uplift on its own: fee contribution from the extra closings, net of what those extra transactions cost to handle.',
      },
      ...COST_ROWS('attorneys and closers'),
      {
        key: 'potential_additional_profit',
        label: 'Potential Additional Profit',
        format: 'money',
        group: 'The bottom line',
        emphasis: true,
        info: 'The extra contribution minus everything JurisTab costs. The single number to take to a planning meeting.',
      },
      {
        key: 'profit_increase_percent',
        label: 'Profit Increase (% of Today)',
        format: 'percent',
        group: 'The bottom line',
        info: 'Potential Additional Profit as a share of today’s Annual Contribution — the profit your closings earn now. 5% means 5% more than today.',
      },
    ],
  },
};
